import {
  AlertTriangle,
  BadgeDollarSign,
  Bell,
  BellRing,
  BookOpenCheck,
  BarChart3,
  Boxes,
  Bot,
  BrainCircuit,
  Building2,
  CalendarClock,
  CalendarRange,
  ChartLine,
  ClipboardCheck,
  ClipboardList,
  FileSignature,
  HeartPulse,
  Images,
  Swords,
  Calculator,
  History,
  Database,
  FileDiff,
  FileQuestion,
  FileCheck2,
  Files,
  Filter,
  Flag,
  FolderKanban,
  Gauge,
  HardHat,
  Landmark,
  Link2,
  Repeat,
  Ruler,
  Coins,
  Cpu,
  Gavel,
  Handshake,
  Scale,
  ShieldPlus,
  Target,
  Banknote,
  CircleDollarSign,
  UserSearch,
  CalendarCheck,
  Percent,
  FileBadge,
  FileUp,
  Briefcase,
  CalendarDays,
  ShieldAlert,
  FileText,
  Clock,
  Award,
  Stethoscope,
  HeartHandshake,
  Contact,
  ScrollText,
  ScanSearch,
  Newspaper,
  Siren,
  Radar,
  ContactRound,
  Timer,
  Inbox,
  LayoutDashboard,
  ListChecks,
  MapPinned,
  Palette,
  Megaphone,
  MessagesSquare,
  Sparkles,
  PanelsTopLeft,
  PiggyBank,
  Plug,
  Radio,
  Receipt,
  Settings,
  ShieldCheck,
  CloudCog,
  HardDrive,
  Laptop,
  MonitorCog,
  Video,
  Wrench,
  Activity,
  Rocket,
  FlaskConical,
  ShoppingCart,
  TrendingUp,
  Truck,
  Users,
  UsersRound,
  Wallet,
  Workflow,
  KeyRound,
  UserCog,
  Settings2,
  SlidersHorizontal,
  CreditCard,
  Lock,
  Archive,
  Network,
  BadgeCheck,
} from 'lucide-react';
import { PERMISSIONS, hasAnyPermission, hasPermission } from '@helios/authz';
import type { NavDestination } from '@/types';
import {
  SUBJECT_HUBS,
  findHubByRailDestinationId,
  isCollapsedNavDestination,
  isHubTabDestinationId,
  type SubjectHub,
} from './hubs';
import type { NavDomain, NavDomainBlueprint } from './types';

/**
 * The navigation registry is the single source of truth for the app shell: it
 * drives the sidebar, the command palette, and the (data-driven) module routes.
 * Every destination is reachable — none is a dead route. Screens delivered by a
 * later sprint render a real ModuleScaffold (persona, purpose, planned
 * capabilities), clearly labeled with their owning sprint. Persona/purpose text
 * is sourced from docs/engineering-bible/06_SCREEN_AND_WORKFLOW_BIBLE.md.
 *
 * `permission` is enforced by S02 in navigation visibility and protected routes.
 *
 * P1 (navigation IA) split this file into two halves:
 *
 *   1. A flat CATALOG of every destination the product owns. Destination
 *      objects are never moved when the information architecture changes, so a
 *      re-grouping can never silently drop a screen.
 *   2. A DOMAIN_BLUEPRINT that arranges operational destinations into the
 *      corporate-department domains the sidebar renders. Changing where a
 *      screen lives is a one-line move in the blueprint, reviewable in
 *      isolation. Subject hubs (`SUBJECT_HUBS`) collapse sibling screens
 *      into one rail row; the child destinations stay in the catalog and
 *      become AUX. Admin / prove-the-build tools are catalogued but reached
 *      from the Settings workspace (`SETTINGS_MENU_DESTINATIONS` plus the
 *      administration pages), not the rail.
 *
 * `NAV_TREE_AUDIT` (asserted by registry.test.ts) proves the two halves stay
 * in agreement: every catalog entry is either in the rail or deliberately
 * auxiliary (`/auth`, the Settings menu, and screens hidden from the rail).
 * Nothing is invented along the way.
 */
const PROJECT_DESTINATIONS: NavDestination[] = [
  {
    id: 'quickbooks-reports',
    label: 'QuickBooks Reports',
    path: '/accounting/reports',
    icon: BarChart3,
    description: 'Financial reports from the connected QuickBooks company.',
    sprint: 'S21_FINANCE_COMMAND_CENTER',
    status: 'live',
    persona: 'Owner / Finance / Accountant',
    purpose: 'Review QuickBooks financial reports with their original rows and reporting basis.',
    capabilities: [
      'QuickBooks report catalog',
      'Date and accounting basis filters',
      'Report evidence and export',
    ],
    permission: PERMISSIONS.REPORTS_LIBRARY_READ,
    allPermissions: [PERMISSIONS.ACCOUNTING_CONNECTIONS_READ],
  },
  {
    id: 'accounting-workspace',
    label: 'QuickBooks Workspace',
    path: '/accounting/workspace',
    icon: Landmark,
    description:
      'Receivables, payables, customer jobs, source documents, and reviewed QuickBooks sync.',
    sprint: 'S27_INTEGRATION_FRAMEWORK',
    status: 'live',
    persona: 'Accountant / Finance / Owner',
    purpose:
      'Work invoices and bills with their job, client, lifecycle evidence, and source records.',
    capabilities: [
      'Receivables and payables with original balances and currencies',
      'Invoice delivery evidence and linked documents',
      'Reviewed job and client associations',
      'Online module access and Desktop connection setup',
    ],
    permission: PERMISSIONS.ACCOUNTING_CONNECTIONS_READ,
    allPermissions: [
      PERMISSIONS.ACCOUNTING_CONNECTIONS_READ,
      PERMISSIONS.ACCOUNTING_ENTITY_LINKS_READ,
    ],
  },
  {
    id: 'finance-taxes',
    label: 'Taxes',
    path: '/finance/taxes',
    icon: FileCheck2,
    description: 'Tax filings, accountant profile, and Microsoft 365 correspondence.',
    sprint: 'S21_FINANCE_COMMAND_CENTER',
    status: 'live',
    persona: 'Owner / Finance / Accountant',
    purpose: 'Keep tax filing documents and accountant communications together.',
    capabilities: ['Tax filing archive', 'Accountant profile', 'Connected mailbox correspondence'],
    // Document downloads and entity filings are gated inside the page; the filing register
    // and accountant profile only need ledger read.
    permission: PERMISSIONS.FINANCE_LEDGER_ACCOUNTS_READ,
  },
  {
    id: 'incoming-bills',
    label: 'Incoming Bills',
    path: '/finance/payables/incoming',
    icon: Inbox,
    description:
      'Every bill, credit and remittance emailed to ap@ and ar@, checked against QuickBooks.',
    sprint: 'S21_FINANCE_COMMAND_CENTER',
    status: 'live',
    persona: 'CFO / Controller / AP',
    purpose: 'No bill ever goes missing: see what arrived by email and is not yet in QuickBooks.',
    capabilities: [
      'Captured from the AP and AR mailbox routes',
      'Extracted vendor, invoice number, dates and totals',
      'QuickBooks cross-check with Not in QuickBooks / Matched / Possible duplicate',
      'Confirm, dismiss, link vendor and re-check',
    ],
    permission: PERMISSIONS.BILL_INTAKE_BILLS_READ,
  },
  {
    id: 'bank-spending',
    label: 'Monthly Costs',
    path: '/banking/spending',
    icon: Repeat,
    description: 'Monthly operating expenses, recurring bills, and evidence of price changes.',
    sprint: 'S21_FINANCE_COMMAND_CENTER',
    status: 'live',
    persona: 'Owner / Finance',
    purpose:
      'Understand recorded expenses and review recurring commitments by account, project, and operational purpose.',
    capabilities: [
      'Recorded monthly expense matrix',
      'Recurring charge history and price changes',
      'Renewal calendar and saved reviews',
    ],
    permission: PERMISSIONS.BANKING_TRANSACTIONS_READ,
    allPermissions: [PERMISSIONS.BANKING_ACCOUNTS_READ],
  },
  {
    id: 'bank-statements',
    label: 'Account Statements',
    path: '/banking/statements',
    icon: FileText,
    description: 'All archived account statements with account filters and PDF review.',
    sprint: 'S21_FINANCE_COMMAND_CENTER',
    status: 'live',
    persona: 'Owner / Finance',
    purpose: 'Review verified uploaded statements across company accounts in one place.',
    capabilities: [
      'Account and year filters',
      'Statement PDF preview and download',
      'Verified manual uploads with period metadata',
    ],
    permission: PERMISSIONS.BANKING_ACCOUNTS_READ,
    allPermissions: [PERMISSIONS.DOCUMENTS_ASSETS_READ],
  },
  // ---- legacy group: Overview ----
  {
    id: 'executive-summary',
    label: 'Executive Overview',
    path: '/',
    icon: Gauge,
    description:
      'Portfolio health, live operations, system status, and the decisions waiting on you.',
    sprint: 'S04_COMMAND_CENTER',
    status: 'live',
    persona: 'CEO / President',
    purpose:
      'Portfolio health, margin, backlog, enterprise risk, strategic actions, map, live ops, system status, and AI insight grids.',
    capabilities: [
      'Portfolio health and margin roll-ups',
      'Revenue and backlog trend analytics',
      'Enterprise risk register',
      'Strategic action recommendations',
      'Portfolio map and live site feeds',
      'Realtime system status and delivery/enterprise AI',
    ],
    permission: PERMISSIONS.DASHBOARD_READ,
  },
  {
    id: 'm365-workspace',
    label: 'M365 Center',
    path: '/workspace',
    icon: CloudCog,
    description: 'Your Agent Desk for Mail, Calendar, To Do, OneNote, OneDrive, and SharePoint.',
    sprint: 'P7_M365_WORKSPACE',
    status: 'live',
    persona: 'Executive / Project Manager',
    purpose:
      'Run your daily communications, schedule, tasks, notes, and Microsoft files from one Agent Desk workspace.',
    capabilities: [
      'Delegated Microsoft OAuth (never shared-mailbox app-only for this screen)',
      'Outlook-style mail folders, reading pane, compose, reply, mark read, and move',
      'Calendar agenda, To Do lists, OneNote pages, OneDrive browser',
      'SharePoint followed/search sites and document libraries',
      'Helios AI activity digest (subjects and titles only)',
      'Sync selected OneDrive items into Document Library',
    ],
    permission: PERMISSIONS.M365_WORKSPACE_READ,
  },
  {
    id: 'google-workspace',
    label: 'Google Workspace',
    path: '/google-workspace',
    icon: CloudCog,
    description:
      'Your Gmail, Calendar, Drive, and company directory via per-user Google sign-in (read-only).',
    sprint: 'P7_GOOGLE_WORKSPACE',
    status: 'live',
    persona: 'Executive / Project Manager',
    purpose:
      'Search mail, check the agenda, pull Drive files into the document library, and look up colleagues without leaving HELIOS.',
    capabilities: [
      'Delegated Google OAuth with PKCE; least-privilege read-only scopes',
      'Gmail search (headers and preview only)',
      'Primary Calendar agenda',
      'Drive browse, search, and import into the Document Library',
      'Workspace directory search',
    ],
    permission: PERMISSIONS.INTEGRATIONS_CONNECTIONS_READ,
  },
  {
    id: 'ops-dashboard',
    label: 'My Day (Operations)',
    path: '/dashboards/ops',
    icon: HardHat,
    description: 'Who is on site, what is blocked, what expires this week, and who to chase.',
    sprint: 'M12_PERSONA_DASHBOARDS',
    status: 'live',
    persona: 'Operations Manager',
    purpose:
      'Answer a vendor’s "who is on the job today" without interrupting anyone, then work the blocked, expiring, and chasing lists.',
    capabilities: [
      'On-site-now roster with the jobs that have filed no daily report',
      'Blocked field tasks, open safety observations, and overdue reports',
      'COIs lapsed or expiring inside 30 days, plus the chase queue',
      'Unanswered quotes, unreturned change orders, and unread client messages',
    ],
    permission: PERMISSIONS.DASHBOARD_READ,
  },
  // ---- legacy group: Projects ----
  {
    id: 'project-portfolio',
    label: 'Project Portfolio',
    path: '/projects',
    icon: FolderKanban,
    description: 'Project table, filters, map, risk rank and portfolio controls.',
    sprint: 'S06_PROJECTS_CORE',
    status: 'live',
    persona: 'Project Executive',
    purpose: 'Project table, filters, map, kanban, risk rank and portfolio controls.',
    capabilities: [
      'Sortable, filterable project table at enterprise scale',
      'Portfolio map and kanban views',
      'Health scoring and risk ranking',
      'Multi-tenant project scoping',
    ],
    permission: PERMISSIONS.PROJECTS_READ,
  },
  {
    id: 'project-overview',
    label: 'Project Overview',
    path: '/projects/overview',
    icon: PanelsTopLeft,
    description: 'Budget, schedule, risks, stakeholders and current tasks.',
    sprint: 'S06_PROJECTS_CORE',
    status: 'live',
    persona: 'Project Team',
    purpose: 'Budget, schedule, risks, stakeholders, current tasks and AI insights.',
    capabilities: [
      'Project header with health and phase',
      'Budget and schedule summaries',
      'Risk and stakeholder registers',
      'Contextual AI insights',
    ],
    permission: PERMISSIONS.PROJECTS_READ,
    railAnyPermissions: [
      PERMISSIONS.PROJECTS_READ,
      PERMISSIONS.FINANCE_BUDGETS_READ,
      PERMISSIONS.SCHEDULING_SCHEDULES_READ,
      PERMISSIONS.DOCUMENTS_RFIS_READ,
      PERMISSIONS.DOCUMENTS_SUBMITTALS_READ,
      PERMISSIONS.DOCUMENTS_CHANGE_ORDERS_READ,
      PERMISSIONS.PORTAL_SELECTION_ITEMS_READ,
    ],
    matchPrefix: true,
  },
  {
    id: 'project-budget',
    label: 'Project Budget',
    path: '/projects/budget',
    icon: Wallet,
    description: 'Budget lines, commitments, actuals, forecast and change exposure.',
    sprint: 'S07_PROJECT_FINANCIALS',
    status: 'live',
    persona: 'Finance / PM',
    purpose: 'Budget lines, commitments, actuals, forecast and change order exposure.',
    capabilities: [
      'Budget line items and cost codes',
      'Commitments and actuals tracking',
      'Forecast at completion',
      'Change order exposure',
    ],
    permission: PERMISSIONS.FINANCE_BUDGETS_READ,
  },
  {
    id: 'project-schedule',
    label: 'Project Schedule',
    path: '/projects/schedule',
    icon: CalendarClock,
    description: 'Gantt, milestones, lookahead, constraints and critical path.',
    sprint: 'S08_PROJECT_SCHEDULES',
    status: 'live',
    persona: 'Scheduler / PM',
    purpose: 'Gantt, milestones, lookahead, constraints, critical path and recovery options.',
    capabilities: [
      'Interactive Gantt and milestones',
      'Three-week lookahead',
      'Critical path and constraints',
      'Schedule recovery options',
    ],
    permission: PERMISSIONS.SCHEDULING_SCHEDULES_READ,
  },
  {
    id: 'rfis',
    label: 'RFIs',
    path: '/projects/rfis',
    icon: FileQuestion,
    description: 'RFI lifecycle, ball-in-court, due dates and cost/schedule impact.',
    sprint: 'S09_PROJECT_CONTROLS',
    status: 'live',
    persona: 'PM / APM',
    purpose: 'RFI lifecycle, ball-in-court, due dates and cost/schedule impact.',
    capabilities: [
      'RFI lifecycle workflow',
      'Ball-in-court tracking',
      'Due dates and overdue alerts',
      'Cost and schedule impact',
    ],
    permission: PERMISSIONS.DOCUMENTS_RFIS_READ,
  },
  {
    id: 'submittals',
    label: 'Submittals',
    path: '/projects/submittals',
    icon: FileCheck2,
    description: 'Submittal packages, review cycles, status and overdue items.',
    sprint: 'S09_PROJECT_CONTROLS',
    status: 'live',
    persona: 'PM / APM',
    purpose: 'Submittal packages, review cycles, status and overdue items.',
    capabilities: [
      'Submittal packages and logs',
      'Review cycles and approvals',
      'Status and overdue tracking',
      'Spec section linkage',
    ],
    permission: PERMISSIONS.DOCUMENTS_SUBMITTALS_READ,
  },
  {
    id: 'change-orders',
    label: 'Change Orders',
    path: '/projects/change-orders',
    icon: FileDiff,
    description: 'Potential, pending, approved, rejected and margin impact.',
    sprint: 'S09_PROJECT_CONTROLS',
    status: 'live',
    persona: 'PM / Finance',
    purpose: 'Potential, pending, approved, rejected, margin impact and client status.',
    capabilities: [
      'Change order pipeline by status',
      'Margin impact analysis',
      'Client approval status',
      'Cost and schedule linkage',
    ],
    permission: PERMISSIONS.DOCUMENTS_CHANGE_ORDERS_READ,
  },
  {
    // M11 STAFF authoring screen. The client-facing portal pages are
    // deliberately NOT in this registry: it drives staff navigation and the
    // command palette, and a client page has no business appearing there.
    id: 'portal-selections',
    label: 'Selections',
    path: '/projects/selections',
    icon: Palette,
    description: 'Client selections, allowances and the decisions behind change orders.',
    sprint: 'M11_CLIENT_PORTAL',
    status: 'live',
    persona: 'PM / Client Executive',
    purpose:
      'Author the selections a client decides on, carry the allowance, and record a decision taken over the phone.',
    capabilities: [
      'Selections and allowances per project',
      'Options with prices and lead times',
      'Over-allowance exposure at a glance',
      'Record a client decision taken by phone',
    ],
    permission: PERMISSIONS.PORTAL_SELECTION_ITEMS_READ,
  },
  // ---- Relationships: canonical identities plus the client and vendor masters ----
  {
    id: 'unified-crm',
    label: 'CRM',
    path: '/crm',
    icon: UsersRound,
    description:
      'Clients, vendors, subcontractors and people: the masters, their source evidence and relationship history.',
    sprint: 'P13_CRM_DEPTH',
    status: 'live',
    persona: 'Executive / Operations / Business Development / Procurement',
    purpose:
      'One tenant identity across clients, vendors, subcontractors and professional contacts with permission-aware provenance, and the native client and vendor workflows that maintain them.',
    capabilities: [
      'Shared relationship directory',
      'Client accounts, contacts, portal and project links',
      'Vendor master with catalogs, price history and CSV ingest',
      'Role-aware source-backed profiles',
      'Reviewed identity and enrichment decisions',
      'Financial, project and activity source references',
    ],
    permission: 'crm:unified:read',
    // Client Accounts and Vendors are tabs of this hub. Holders of either
    // native grant keep the rail row and land on the first tab they may open.
    railAnyPermissions: [
      'crm:unified:read',
      PERMISSIONS.CRM_CLIENT_ACCOUNTS_READ,
      PERMISSIONS.PROCUREMENT_VENDORS_READ,
    ],
    matchPrefix: true,
  },
  {
    // Formerly the standalone Client Accounts page (/clients). Same native
    // writers, grants, concurrency checks and audit; now a Relationships tab.
    id: 'crm-client-accounts',
    label: 'Clients',
    path: '/crm/clients',
    icon: UsersRound,
    description: 'Client accounts, contacts, portal access and project history.',
    sprint: 'M02_CRM_LITE',
    status: 'live',
    persona: 'Executive / Business Development',
    purpose: 'Client account registry with contacts, payment posture, and linked project history.',
    capabilities: [
      'Client account registry with status pipeline',
      'Contacts with a primary designation per account',
      'Project linking and relationship history',
      'Version-checked edits with audit trail',
    ],
    permission: PERMISSIONS.CRM_CLIENT_ACCOUNTS_READ,
    matchPrefix: true,
  },
  {
    // Formerly the standalone Vendors hub (/vendors). Same native catalog,
    // ingest, portal, payment and compliance controls; now a Relationships tab.
    id: 'vendors',
    label: 'Vendors & subcontractors',
    path: '/crm/vendors',
    icon: Truck,
    description:
      'QBO-linked COS vendors and their catalog. Prices are versioned; CSV ingest is dry-run then human-confirmed.',
    sprint: 'P15_VENDOR_CATALOG',
    status: 'live',
    persona: 'Procurement / Integrations / Owner',
    purpose:
      'Find the vendors QuickBooks Import already stamped on AccountingEntityLink, then hold SKU catalogs with price history.',
    capabilities: [
      'Directory of LINKED Vendor rows only — no second master',
      'Catalog child table with SKU + UOM uniqueness per vendor',
      'CSV dry-run diff and human confirm before commit',
      'Price versions keep effectiveFrom / effectiveTo history',
    ],
    permission: PERMISSIONS.PROCUREMENT_VENDORS_READ,
    matchPrefix: true,
  },
  {
    id: 'vendors-catalog-ingest',
    label: 'Catalog Ingest',
    path: '/crm/vendors/ingest',
    icon: Files,
    description: 'CSV dry-run and confirmed upsert into a linked vendor catalog.',
    sprint: 'P15_VENDOR_CATALOG',
    status: 'live',
    persona: 'Procurement / Integrations',
    purpose:
      'Upload a supplier CSV, review the diff, and confirm before any catalog price version is written.',
    capabilities: [
      'CSV and MANUAL sources',
      'Dry-run persisted with lineage',
      'Confirm stamps confirmedAt before commit',
    ],
    permission: PERMISSIONS.PROCUREMENT_VENDORS_READ,
  },
  {
    // The canonical vendor/subcontractor identity lens. It shares the
    // Vendors & subcontractors tab with the vendor master and is the tab's
    // target for CRM readers who do not hold procurement:vendors:read.
    id: 'crm-vendor-identities',
    label: 'Vendor & subcontractor identities',
    path: '/crm/vendor',
    icon: Handshake,
    description: 'Canonical vendor and subcontractor identities with source evidence.',
    sprint: 'P13_CRM_DEPTH',
    status: 'live',
    persona: 'Executive / Operations / Procurement',
    permission: 'crm:unified:read',
  },
  // ---- legacy group: Preconstruction ----
  {
    id: 'lead-funnel',
    label: 'Lead Funnel',
    path: '/estimating/leads',
    icon: Filter,
    description: 'Inbound bid invitations scored against your go/no-go criteria.',
    sprint: 'M04_ESTIMATING_I',
    status: 'live',
    persona: 'Chief Estimator / Owner',
    purpose:
      'Score every inbound bid invitation against the tenant go/no-go criteria and record an explicit human decision.',
    capabilities: [
      'Kanban funnel across every lead status',
      'Deterministic viability score with a per-criterion explanation',
      'Qualify / decline / no-bid with a required, audited reason',
      'Simulated inbound plan-room feed for demonstration',
    ],
    permission: PERMISSIONS.ESTIMATING_LEADS_READ,
  },
  {
    id: 'cost-database',
    label: 'Cost Database',
    path: '/estimating/cost-database',
    icon: Database,
    description: 'Priced units and assemblies with effective-dated price history.',
    sprint: 'M04_ESTIMATING_I',
    status: 'live',
    persona: 'Chief Estimator',
    purpose:
      'Maintain the tenant cost database and assemblies with a preserved, effective-dated price history.',
    capabilities: [
      'Cost item catalog with current-price filtering',
      'Effective-dated price history drawer',
      'Price revisions recorded as new rows, never overwrites',
      'Assemblies with component lists and current pricing',
    ],
    permission: PERMISSIONS.ESTIMATING_COST_ITEMS_READ,
  },
  {
    id: 'estimates',
    label: 'Estimates',
    path: '/estimating/estimates',
    icon: Calculator,
    description: 'Reviewed company cost revisions, their source layers and the estimate workspace.',
    sprint: 'M04_ESTIMATING_I',
    status: 'live',
    persona: 'Chief Estimator / Project Executive',
    purpose:
      'Review source-backed company cost and preserve the line-by-line estimate used to prepare it.',
    capabilities: [
      'Immutable cost revisions with explicit scope and currency',
      'Approved materials, labour and equipment source layers',
      'Independent review, approval and source-change blockers',
      'Exact cost build-up and source-linked estimate details',
    ],
    permission: PERMISSIONS.ESTIMATING_ESTIMATES_READ,
    railAnyPermissions: [
      PERMISSIONS.ESTIMATING_ESTIMATES_READ,
      PERMISSIONS.ESTIMATING_PIPELINE_READ,
      PERMISSIONS.BID_INTAKE_INVITATIONS_READ,
      PERMISSIONS.ESTIMATING_LEADS_READ,
      PERMISSIONS.ESTIMATING_COST_ITEMS_READ,
      PERMISSIONS.TAKEOFF_DRAWING_SETS_READ,
      PERMISSIONS.COMPING_RUNS_READ,
      PERMISSIONS.LABOR_PLANS_READ,
      PERMISSIONS.BIDDING_ASSEMBLIES_READ,
      PERMISSIONS.BIDDING_PATTERNS_READ,
      PERMISSIONS.ESTIMATING_ANALYTICS_READ,
      PERMISSIONS.ESTIMATING_SETTINGS_READ,
      PERMISSIONS.ESTIMATING_CRITERIA_PROFILES_READ,
    ],
    matchPrefix: true,
  },
  {
    id: 'bid-history',
    label: 'Bid History',
    path: '/estimating/history',
    icon: History,
    description: 'Hit rate, loss margin, and tier performance across recorded bid results.',
    sprint: 'M05_ESTIMATING_II',
    status: 'live',
    persona: 'Chief Estimator / Owner',
    purpose:
      'Show how far behind the winning number the losing bids actually landed, and which tier wins.',
    capabilities: [
      'Overall hit rate and the average loss margin against the winner',
      'Hit rate broken out by sector, county, and client',
      'Tier performance: which bid tier actually wins',
      'Deterministic recommendations derived from the recorded results',
    ],
    permission: PERMISSIONS.ESTIMATING_ANALYTICS_READ,
    allPermissions: [PERMISSIONS.ESTIMATING_ESTIMATES_READ],
  },
  // ---- legacy group: Intelligence ----
  {
    id: 'digital-twin',
    label: 'Digital Twin Viewer',
    path: '/digital-twins',
    icon: Boxes,
    description: '3D BIM models, zones, operational overlays and issue markers.',
    sprint: 'S10_DIGITAL_TWIN_CORE',
    status: 'live',
    persona: 'Executive / Field',
    purpose: '3D BIM models, model revisions, zones, issue markers and operational overlays.',
    capabilities: [
      '3D / BIM model viewer',
      'Floor, system and overlay toggles',
      'Issue markers and clickable zones',
      'Model revision timeline and camera controls',
    ],
    permission: PERMISSIONS.DIGITAL_TWINS_BIM_MODELS_READ,
  },
  {
    id: 'site-telemetry',
    label: 'Site Telemetry',
    path: '/site-telemetry',
    icon: Radio,
    description: 'Drone scans, sensors, camera feeds, and progress snapshots.',
    sprint: 'S11_SITE_TELEMETRY',
    status: 'live',
    persona: 'Field / PM',
    purpose: 'Governed site capture records for drones, sensors, cameras, and progress.',
    capabilities: [
      'Drone scan registry',
      'Sensor devices and readings',
      'Progress snapshots',
      'Camera feed provisioning without simulated video',
    ],
    permission: PERMISSIONS.DIGITAL_TWINS_DRONE_SCANS_READ,
  },
  {
    id: 'ai-agents',
    label: 'AI Agents',
    path: '/ai-agents',
    icon: Bot,
    description: 'Agent registry, autonomy, tasks, recommendations and logs.',
    sprint: 'S12_AI_AGENT_FOUNDATION',
    status: 'live',
    persona: 'Admin / Executives',
    purpose: 'Agent registry, autonomy, tasks, recommendations, logs and permissions.',
    capabilities: [
      'Autonomous agent registry',
      'Autonomy and approval thresholds',
      'Tasks, runs and recommendations',
      'Agent audit logs',
    ],
    permission: PERMISSIONS.AI_AGENTS_READ,
    matchPrefix: true,
  },
  {
    id: 'agent-detail',
    label: 'Agent Detail',
    path: '/ai-agents/detail',
    icon: BrainCircuit,
    description: 'Mission, data sources, actions, confidence and approval thresholds.',
    sprint: 'S13_EXECUTIVE_ADVISOR_AGENT',
    status: 'live',
    persona: 'Admin / Executive',
    purpose: 'Mission, data sources, tasks, actions, settings, confidence and approval thresholds.',
    capabilities: [
      'Agent mission and data sources',
      'Task and action history',
      'Confidence scoring',
      'Approval threshold settings',
    ],
    permission: PERMISSIONS.AI_AGENTS_READ,
  },
  {
    id: 'finance-agent-detail',
    label: 'Finance Agent Detail',
    path: '/ai-agents/finance',
    icon: Wallet,
    description: 'CFO finance agent margin, cash, WIP, and AR/AP risk recommendations.',
    sprint: 'S14_FINANCE_AGENT',
    status: 'live',
    persona: 'CFO / Finance Manager',
    purpose:
      'Margin, cash burn, WIP billing gaps, AP backlog, and open forecast risk recommendations.',
    capabilities: [
      'Margin and cash burn monitoring',
      'WIP underbilling detection',
      'AP backlog and forecast risk alerts',
      'CFO approval workflow',
    ],
    permission: PERMISSIONS.AI_AGENTS_READ,
  },
  {
    id: 'scheduling-agent-detail',
    label: 'Scheduling Agent Detail',
    path: '/ai-agents/scheduling',
    icon: CalendarClock,
    description: 'Delay prediction, resequencing, and schedule recovery recommendations.',
    sprint: 'S15_SCHEDULING_AGENT',
    status: 'live',
    persona: 'Scheduler / COO',
    purpose:
      'Critical path float, baseline slippage, milestone risk, constraints, and recovery recommendations.',
    capabilities: [
      'Critical path and float monitoring',
      'Baseline slippage detection',
      'Milestone and constraint risk alerts',
      'Scheduler approval workflow',
    ],
    permission: PERMISSIONS.AI_AGENTS_READ,
  },
  {
    id: 'procurement-agent-detail',
    label: 'Procurement Agent Detail',
    path: '/ai-agents/procurement',
    icon: ShoppingCart,
    description: 'Supplier risk, lead time, and price opportunity recommendations.',
    sprint: 'S16_PROCUREMENT_AGENT',
    status: 'live',
    persona: 'Procurement Manager',
    purpose:
      'Long-lead slippage, supplier concentration, uninvoiced exposure, price opportunities, and procurement alerts.',
    capabilities: [
      'Long-lead and schedule slip monitoring',
      'Supplier concentration detection',
      'Procurement exposure and price opportunity alerts',
      'Procurement Manager approval workflow',
    ],
    permission: PERMISSIONS.AI_AGENTS_READ,
  },
  {
    id: 'safety-agent-detail',
    label: 'Safety Agent Detail',
    path: '/ai-agents/safety',
    icon: HardHat,
    description: 'Incident clusters, overdue inspections, and open finding urgency.',
    sprint: 'S17_SAFETY_COMPLIANCE_AGENTS',
    status: 'live',
    persona: 'Safety Manager',
    purpose:
      'Incident monitoring, inspection compliance, PPE risk, open findings, and high-risk site alerts.',
    capabilities: [
      'Incident cluster and lookback monitoring',
      'Overdue inspection detection',
      'Open finding urgency alerts',
      'Safety Manager approval workflow',
    ],
    permission: PERMISSIONS.AI_AGENTS_READ,
  },
  {
    id: 'compliance-agent-detail',
    label: 'Compliance Agent Detail',
    path: '/ai-agents/compliance',
    icon: ShieldCheck,
    description: 'Permit expiry, insurance coverage, and regulatory constraint risk.',
    sprint: 'S17_SAFETY_COMPLIANCE_AGENTS',
    status: 'live',
    persona: 'Compliance Manager',
    purpose:
      'Permit and insurance expiry tracking, regulatory constraints, OSHA obligations, and portfolio permit risk.',
    capabilities: [
      'Permit and insurance expiry monitoring',
      'Regulatory constraint urgency alerts',
      'Portfolio permit risk detection',
      'Compliance Manager approval workflow',
    ],
    permission: PERMISSIONS.AI_AGENTS_READ,
  },
  {
    id: 'estimating-agent-detail',
    label: 'Estimating Agent Detail',
    path: '/ai-agents/estimating',
    icon: Calculator,
    description: 'Quantity variance, contingency gaps, and bid pricing risk.',
    sprint: 'S18_ESTIMATING_QA_AGENTS',
    status: 'live',
    persona: 'Estimator',
    purpose:
      'Bid package risk pricing, quantity takeoff variance, margin erosion, and pursuit pipeline recovery.',
    capabilities: [
      'Quantity variance and contingency monitoring',
      'Margin erosion and bid due-date alerts',
      'Estimating budget variance detection',
      'Estimator approval workflow',
    ],
    permission: PERMISSIONS.AI_AGENTS_READ,
  },
  {
    id: 'qa-qc-agent-detail',
    label: 'QA/QC Agent Detail',
    path: '/ai-agents/qa-qc',
    icon: ClipboardCheck,
    description: 'Inspection deficiencies, punch list backlog, and warranty risk.',
    sprint: 'S18_ESTIMATING_QA_AGENTS',
    status: 'live',
    persona: 'Quality Manager',
    purpose:
      'QA inspection compliance, punch list urgency, warranty exposure, and deficiency clustering.',
    capabilities: [
      'Overdue and failed QA inspection detection',
      'Punch list and warranty risk monitoring',
      'Portfolio punch backlog escalation',
      'Quality Manager approval workflow',
    ],
    permission: PERMISSIONS.AI_AGENTS_READ,
  },
  {
    id: 'hr-workforce-agent-detail',
    label: 'HR Workforce Agent Detail',
    path: '/ai-agents/hr-workforce',
    icon: Users,
    description: 'Crew staffing gaps, certification expiry, and labor allocation risk.',
    sprint: 'S19_RESOURCE_EQUIPMENT_AGENTS',
    status: 'live',
    persona: 'HR Manager',
    purpose:
      'Labor demand forecasting, crew assignment gaps, certification compliance, and portfolio staffing recovery.',
    capabilities: [
      'Understaffed crew detection',
      'Certification expiry monitoring',
      'Workforce constraint urgency alerts',
      'HR Manager approval workflow',
    ],
    permission: PERMISSIONS.AI_AGENTS_READ,
  },
  {
    id: 'equipment-agent-detail',
    label: 'Equipment Agent Detail',
    path: '/ai-agents/equipment',
    icon: Boxes,
    description: 'Fleet utilization, maintenance overdue, and rental return risk.',
    sprint: 'S19_RESOURCE_EQUIPMENT_AGENTS',
    status: 'live',
    persona: 'Equipment Manager',
    purpose:
      'Equipment utilization optimization, maintenance scheduling, rental fleet tracking, and idle fleet recovery.',
    capabilities: [
      'Low and high utilization detection',
      'Overdue maintenance alerts',
      'Rental return urgency monitoring',
      'Equipment Manager approval workflow',
    ],
    permission: PERMISSIONS.AI_AGENTS_READ,
  },
  {
    id: 'market-intelligence-agent-detail',
    label: 'Market Intelligence Agent Detail',
    path: '/ai-agents/market-intelligence',
    icon: TrendingUp,
    description: 'Bid pipeline, permits, competitors and regional opportunities.',
    sprint: 'S20_MARKET_GROWTH_MA_AGENTS',
    status: 'live',
    persona: 'Strategy',
    purpose:
      'Bid urgency, competitor pressure, sector/region opportunity qualification, and pursuit capacity recovery.',
    capabilities: [
      'Bid due urgency detection',
      'Competitor pressure monitoring',
      'Large opportunity qualification',
      'Strategy Lead approval workflow',
    ],
    permission: PERMISSIONS.AI_AGENTS_READ,
  },
  {
    id: 'growth-ma-agent-detail',
    label: 'Growth & M&A Agent Detail',
    path: '/ai-agents/growth-ma',
    icon: Building2,
    description: 'Acquisition targets, scoring, diligence and expansion scenarios.',
    sprint: 'S20_MARKET_GROWTH_MA_AGENTS',
    status: 'live',
    persona: 'CEO / CFO / M&A',
    purpose:
      'Acquisition target scoring, diligence risk management, synergy readiness, and expansion ROI prioritization.',
    capabilities: [
      'Strategic fit scoring',
      'Scorecard risk escalation',
      'Diligence cadence monitoring',
      'CEO/M&A Lead approval workflow',
    ],
    permission: PERMISSIONS.AI_AGENTS_READ,
  },
  {
    id: 'market-intelligence',
    label: 'Market Intelligence',
    path: '/market-intelligence',
    icon: TrendingUp,
    description: 'Local construction reports, operating-area news and business relationship risks.',
    sprint: 'S20_MARKET_GROWTH_MA_AGENTS',
    status: 'live',
    persona: 'Strategy',
    purpose:
      'ZIP-based construction intelligence with original evidence and monitored business risks.',
    capabilities: [
      'ZIP and multi-area operating footprints',
      'Official construction, housing and economic reports',
      'Vendor, client and customer risk monitoring',
      'Source provenance, freshness and review history',
    ],
    permission: PERMISSIONS.DASHBOARD_READ,
  },
  {
    id: 'growth-ma',
    label: 'Growth & M&A',
    path: '/growth-ma',
    icon: Building2,
    description: 'Acquisition targets, scoring, diligence and synergies.',
    sprint: 'S20_MARKET_GROWTH_MA_AGENTS',
    status: 'live',
    persona: 'CEO / CFO / M&A',
    purpose: 'Acquisition targets, scoring, diligence, synergies and expansion scenarios.',
    capabilities: [
      'Acquisition target scoring',
      'Diligence workspace',
      'Synergy modeling',
      'Expansion scenarios',
    ],
    permission: PERMISSIONS.DASHBOARD_READ,
  },
  // ---- legacy group: Command Centers ----
  {
    id: 'finance',
    label: 'Finance',
    path: '/finance',
    icon: BadgeDollarSign,
    description: 'Revenue, margin, WIP, AR/AP, cash flow and profitability.',
    sprint: 'S21_FINANCE_COMMAND_CENTER',
    status: 'live',
    persona: 'CFO / Controller',
    purpose: 'Revenue, margin, WIP, AR/AP, cash flow and project profitability.',
    capabilities: [
      'Revenue and margin dashboards',
      'WIP and cash flow',
      'AR/AP aging',
      'Project profitability',
    ],
    permission: PERMISSIONS.FINANCE_BUDGETS_READ,
    railAnyPermissions: [
      PERMISSIONS.FINANCE_BUDGETS_READ,
      PERMISSIONS.FINANCE_BILLING_APPLICATIONS_READ,
      PERMISSIONS.FINANCE_RETAINAGE_RECORDS_READ,
      PERMISSIONS.ACCOUNTING_JOB_COST_EXPORTS_READ,
      PERMISSIONS.FINANCE_LEDGER_ACCOUNTS_READ,
      PERMISSIONS.FINANCE_AR_INVOICES_READ,
      PERMISSIONS.FINANCE_AP_INVOICES_READ,
      PERMISSIONS.BANKING_ACCOUNTS_READ,
      PERMISSIONS.BANKING_TRANSACTIONS_READ,
      PERMISSIONS.BANKING_RECONCILIATION_READ,
      PERMISSIONS.ACCOUNTING_ENTITY_LINKS_READ,
      PERMISSIONS.ACCOUNTING_SYNC_RUNS_READ,
    ],
    matchPrefix: true,
  },
  {
    id: 'billing-applications',
    label: 'Pay Applications',
    path: '/finance/billing',
    icon: Receipt,
    description: 'AIA G702/G703 pay applications that build themselves from the job.',
    sprint: 'M06_AIA_BILLING',
    status: 'live',
    persona: 'Finance / Controller',
    purpose:
      'Generate, certify, and print AIA G702/G703 pay applications without re-keying a schedule of values each month.',
    capabilities: [
      'Schedule of values from the budget and accepted change orders',
      'Roll-forward from the prior application',
      'G702 certificate and G703 continuation sheet PDF',
      'Approve, submit, and record payment',
    ],
    permission: PERMISSIONS.FINANCE_BILLING_APPLICATIONS_READ,
  },
  {
    id: 'billing-collections',
    label: 'Collections',
    path: '/finance/collections',
    icon: Wallet,
    description: 'Owner/developer AR aged from certified pay applications.',
    sprint: 'M06_AIA_BILLING',
    status: 'live',
    persona: 'CEO / Controller',
    purpose:
      'See which certified AIA draws are still uncollected, whether they have been pushed to QuickBooks, and which retainage holds need a follow-up — without inventing balances.',
    capabilities: [
      'Aging board from stored submit / approve / period-to dates',
      'Payment status from BillingApplication status and paidAt',
      'One-click push of approved pay apps to QBO',
      'Retainage follow-up from existing holds',
    ],
    permission: PERMISSIONS.FINANCE_BILLING_APPLICATIONS_READ,
  },
  {
    id: 'retainage-register',
    label: 'Retainage',
    path: '/finance/retainage',
    icon: PiggyBank,
    description: 'How much of our money is being held, and on which jobs.',
    sprint: 'M06_AIA_BILLING',
    status: 'live',
    persona: 'Finance / Controller',
    purpose:
      'A single across-jobs view of retainage held, released, and outstanding, with how long the oldest money has been out.',
    capabilities: [
      'Held / released / outstanding per project',
      'Oldest-held ageing',
      'Release recording',
    ],
    permission: PERMISSIONS.FINANCE_RETAINAGE_RECORDS_READ,
  },
  {
    id: 'finance-analytics',
    label: 'Finance Analytics',
    path: '/finance/analytics',
    icon: ChartLine,
    description:
      'Margin trend, cash position, work in progress, retainage ageing and the pay-application pipeline — every chart drillable.',
    sprint: 'M15_ANALYTICS_VISUALIZATION',
    status: 'live',
    persona: 'Owner / Finance',
    purpose:
      'Turn the finance line items into shapes a person can read at a glance, and let them click any shape to reach the records underneath it.',
    capabilities: [
      'Portfolio margin trend from WIP snapshots, gaps drawn as gaps',
      'AR against AP by ageing bucket',
      'Billed against earned revenue per job',
      'Retainage outstanding by age and by job',
      'Pay-application pipeline by status',
    ],
    permission: PERMISSIONS.FINANCE_BUDGETS_READ,
  },
  {
    id: 'job-cost-export',
    label: 'Job Cost',
    path: '/finance/job-cost-export',
    icon: HardHat,
    description: 'Labor, material, subcontract, equipment and other — per cost code, per period.',
    sprint: 'M13_ACCOUNTING_PAYROLL',
    status: 'live',
    persona: 'Owner / Finance',
    purpose:
      'The job-costing report a contractor loses when they leave a construction ERP: spend split five ways per cost code, with budget and variance, printable for any period.',
    capabilities: [
      'Five-way cost split per cost code',
      'Period spend against cost to date',
      'Budget and variance per code',
      'CSV export to the document library',
    ],
    permission: PERMISSIONS.ACCOUNTING_JOB_COST_EXPORTS_READ,
  },
  {
    id: 'finance-subjects',
    label: 'Customers & Vendors',
    path: '/finance/subjects',
    icon: Handshake,
    description:
      'ClientAccounts, Vendors, Projects and CostCodes already in ConstructionOS, with their QuickBooks AccountingEntityLink stamps.',
    sprint: 'M13_ACCOUNTING_PAYROLL',
    status: 'live',
    persona: 'CFO / Controller',
    purpose:
      'See the names QuickBooks Import wrote into ConstructionOS without hunting Mappings, and tell zero COS rows from zero LINKED stamps.',
    capabilities: [
      'Customers from ClientAccount',
      'Vendors from Vendor',
      'Projects from QBO Class import',
      'Cost codes from QBO Item import',
      'LINKED vs unlinked AccountingEntityLink badges',
    ],
    permission: PERMISSIONS.FINANCE_BUDGETS_READ,
    anyPermissions: [
      PERMISSIONS.FINANCE_BUDGETS_READ,
      PERMISSIONS.ACCOUNTING_ENTITY_LINKS_READ,
      PERMISSIONS.CRM_CLIENT_ACCOUNTS_READ,
      PERMISSIONS.PROCUREMENT_VENDORS_READ,
      PERMISSIONS.PROJECTS_READ,
      PERMISSIONS.FINANCE_COST_CODES_READ,
    ],
  },
  {
    // Renamed/repointed from the standalone "Payroll Hours" screen to the
    // parent of the Payroll workspace (Hours / Cost / Certified / Wage
    // Schedules tabs) — same pattern as Bank Accounts below. Id kept
    // stable so no catalog churn ripples into permissions or tests beyond
    // the path.
    id: 'payroll-exports',
    label: 'Payroll',
    path: '/payroll',
    icon: Timer,
    description:
      'Hours, stored-rate cost, certified payroll, and the wage schedules they are priced against — one workspace.',
    sprint: 'M13_ACCOUNTING_PAYROLL',
    status: 'live',
    persona: 'Finance / Office Manager',
    purpose:
      'Hand a payroll provider per-employee, per-day, per-cost-code hours without re-keying timesheets, file certified payroll for prevailing-wage work, and hold the schedules it is priced against. HELIOS owns hours; it does not calculate tax, file returns, or move money.',
    capabilities: [
      'Regular and overtime split at 40 hours a week',
      'Prevailing-wage flagging and a WH-347-format sheet',
      'An explicit "not included" panel',
      'CSV export and delivery recording',
    ],
    permission: PERMISSIONS.PAYROLL_EXPORTS_READ,
    railAnyPermissions: [
      PERMISSIONS.PAYROLL_EXPORTS_READ,
      PERMISSIONS.PAYROLL_CERTIFIED_PAYROLL_READ,
      PERMISSIONS.PAYROLL_WAGE_SCHEDULES_READ,
    ],
    matchPrefix: true,
  },
  {
    id: 'payroll-cost',
    label: 'Cost',
    path: '/payroll/cost',
    icon: Wallet,
    description: 'Per-run and period payroll cost from stored ConstructionOS hours and rates.',
    sprint: 'M13_ACCOUNTING_PAYROLL',
    status: 'live',
    persona: 'Finance / Controller',
    purpose:
      'See what a payroll run costs in stored hours and rates so Meyer can judge provider volume — without inventing Intuit fees or pulling QuickBooks Payroll.',
    capabilities: [
      'Per-run hours and stored-rate gross',
      'Period rollup by project and crew',
      'EMPLOYEE and TimeActivity AccountingEntityLink coverage',
      'Honest empty when no exports or no QBO Employee links',
    ],
    permission: PERMISSIONS.PAYROLL_EXPORTS_READ,
  },
  {
    id: 'certified-payroll',
    label: 'Certified Payroll',
    path: '/payroll/certified',
    icon: FileCheck2,
    description:
      'Certified payroll for public work — each worker against the wage determination that governs their job.',
    sprint: 'P5_PREVAILING_WAGE',
    status: 'live',
    persona: 'Finance / Office Manager',
    purpose:
      'File certified payroll for prevailing-wage work without assembling it by hand, and refuse to submit a line nobody can judge.',
    capabilities: [
      'Base and supplement compared separately, never blended',
      'A rate not on file stays absent, never zero',
      'Submission refused by name for any unjudgeable line',
      'Determination on record replaces the sector guess',
    ],
    permission: PERMISSIONS.PAYROLL_CERTIFIED_PAYROLL_READ,
  },
  {
    id: 'wage-schedules',
    label: 'Wage Schedules',
    path: '/payroll/wage-schedules',
    icon: BookOpenCheck,
    description:
      'Prevailing-wage rates by trade, county and effective date, and the jobs each determination governs.',
    sprint: 'P5_PREVAILING_WAGE',
    status: 'live',
    persona: 'Finance / Estimator',
    purpose:
      'Hold the schedules a public job is priced and paid against, so a lapsed rate is visible before it is used.',
    capabilities: [
      'Rates by classification, kind, apprentice term and window',
      'Effective-dated, so a lapsed schedule cannot be selected',
      'Counties with no schedule on file are shown as such',
      'Determinations linked to the jobs they govern',
    ],
    permission: PERMISSIONS.PAYROLL_WAGE_SCHEDULES_READ,
  },
  {
    id: 'procurement',
    label: 'Procurement',
    path: '/procurement',
    icon: ShoppingCart,
    description: 'Materials, vendors, POs, lead time risk and exposure.',
    sprint: 'S22_PROCUREMENT_COMMAND_CENTER',
    status: 'live',
    persona: 'Procurement',
    purpose: 'Materials, vendors, POs, lead time risk and procurement exposure.',
    capabilities: [
      'Materials and vendor management',
      'Purchase orders',
      'Lead-time risk',
      'Procurement exposure',
    ],
    permission: PERMISSIONS.PROCUREMENT_VENDORS_READ,
  },
  {
    id: 'scheduling',
    label: 'Scheduling',
    path: '/scheduling',
    icon: CalendarRange,
    description: 'Critical path, constraints, milestones and recovery simulations.',
    sprint: 'S23_SCHEDULING_COMMAND_CENTER',
    status: 'live',
    persona: 'Scheduler / Operations',
    purpose: 'Critical path, constraints, milestones and recovery simulations.',
    capabilities: [
      'Enterprise critical path',
      'Constraint management',
      'Milestone tracking',
      'Recovery simulations',
    ],
    permission: PERMISSIONS.SCHEDULING_SCHEDULES_READ,
  },
  {
    id: 'safety',
    label: 'Safety',
    path: '/safety',
    icon: HardHat,
    description: 'TRIR, incidents, inspections, training and compliance alerts.',
    sprint: 'S24_SAFETY_QUALITY_CENTER',
    status: 'live',
    persona: 'Safety',
    purpose: 'TRIR, incidents, inspections, training, site heatmap and compliance alerts.',
    capabilities: [
      'TRIR and incident tracking',
      'Inspections and training',
      'Site safety heatmap',
      'Compliance alerts',
    ],
    permission: PERMISSIONS.SAFETY_INCIDENTS_READ,
  },
  {
    id: 'quality',
    label: 'Quality',
    path: '/quality',
    icon: ClipboardCheck,
    description: 'Punch list, QA/QC status, deficiencies, warranty and inspections.',
    sprint: 'S24_SAFETY_QUALITY_CENTER',
    status: 'live',
    persona: 'Quality',
    purpose: 'Punch list, QA/QC status, deficiencies, warranty and inspections.',
    capabilities: [
      'Punch list management',
      'QA/QC status',
      'Deficiency tracking',
      'Warranty and inspections',
    ],
    permission: PERMISSIONS.QUALITY_INSPECTIONS_READ,
  },
  {
    id: 'approvals',
    label: 'Approvals',
    path: '/approvals',
    icon: ClipboardCheck,
    description: 'Cross-module approval queue, decisions, and the full audit trail.',
    sprint: 'P16_APPROVALS_HUB',
    status: 'live',
    persona: 'Operations / Executives',
    purpose:
      'One place to file, review, and decide purchase, bid, job, and AI-agent approval requests.',
    capabilities: [
      'Create a request against a source record',
      'Approve or reject with a required reason',
      'Filterable audit trail of every decision',
      'Deep link from Helios AI recommendation cards',
    ],
    permission: PERMISSIONS.APPROVALS_READ,
  },
  {
    id: 'resources',
    label: 'Resources',
    path: '/resources',
    icon: Users,
    description: 'Labor, crews, equipment, workforce and asset utilization.',
    sprint: 'S25_RESOURCE_CENTER',
    status: 'live',
    persona: 'COO / Operations',
    purpose: 'Labor, crews, equipment, workforce availability and asset utilization.',
    capabilities: [
      'Labor and crew allocation',
      'Equipment and asset utilization',
      'Workforce availability',
      'Utilization dashboards',
    ],
    permission: PERMISSIONS.DASHBOARD_READ,
  },
  // ---- legacy group: Field ----
  {
    id: 'field-today',
    label: 'Today',
    path: '/field',
    icon: HardHat,
    description: "The superintendent's phone: today's jobs, today's report, my tasks.",
    sprint: 'M08_FIELD_OPERATIONS',
    status: 'live',
    persona: 'Superintendent / Field Worker',
    purpose:
      "Start or continue today's daily report on a phone, with open tasks and unresolved observations in one screen.",
    capabilities: [
      'One-thumb daily report capture',
      'Start or resume today for each dispatched job',
      'My open tasks',
      'Unresolved observations on today’s jobs',
    ],
    permission: PERMISSIONS.FIELD_DAILY_REPORTS_READ,
    railAnyPermissions: [
      PERMISSIONS.FIELD_DAILY_REPORTS_READ,
      PERMISSIONS.FIELD_DISPATCHES_READ,
      PERMISSIONS.FIELD_FIELD_TASKS_READ,
      PERMISSIONS.FIELD_OBSERVATIONS_READ,
      PERMISSIONS.DOCUMENTS_RFIS_READ,
    ],
    matchPrefix: true,
  },
  {
    id: 'field-daily-reports',
    label: 'Daily Reports',
    path: '/field/daily-reports',
    icon: ClipboardCheck,
    description: 'Every day on every job, searchable — review and approve what the field filed.',
    sprint: 'M08_FIELD_OPERATIONS',
    status: 'live',
    persona: 'Project Manager / Superintendent',
    purpose:
      'Search the field log by date, project, and text, then review and approve submitted reports.',
    capabilities: [
      'Weather, labor, equipment, quantities, and photos per day',
      'Submit / approve / reopen lifecycle',
      'Full-text search across the log',
      'Separate approve permission from the filing permission',
    ],
    permission: PERMISSIONS.FIELD_DAILY_REPORTS_READ,
  },
  {
    id: 'field-dispatch',
    label: 'Crew Dispatch',
    path: '/field/dispatch',
    icon: Truck,
    description: 'Who is going where today, and who is already on site.',
    sprint: 'M08_FIELD_OPERATIONS',
    status: 'live',
    persona: 'Operations Manager / Superintendent',
    purpose:
      'Plan the day once, then answer "who is on site" in one glance instead of phoning the CEO.',
    capabilities: [
      'Day view of crews by project',
      'Assign a roster crew and named foreman (persisted)',
      'Confirm, check in, and complete',
      'On-site-now rail with foreman contact',
      'Flags jobs with crews but no daily report',
    ],
    permission: PERMISSIONS.FIELD_DISPATCHES_READ,
  },
  {
    id: 'field-observations',
    label: 'Observations',
    path: '/field/observations',
    icon: AlertTriangle,
    description: 'Safety, quality, and progress observations with a durable history.',
    sprint: 'M08_FIELD_OPERATIONS',
    status: 'live',
    persona: 'Safety Manager / Superintendent',
    purpose:
      'Raise, assign, review, and resolve what the field saw, so "when did that happen?" is a query rather than an archaeology dig.',
    capabilities: [
      'Type and severity classification',
      'Open / in review / resolved lifecycle',
      'Recorded resolution notes',
      'Full-text search',
    ],
    permission: PERMISSIONS.FIELD_OBSERVATIONS_READ,
  },
  {
    id: 'field-rfis',
    label: 'RFIs',
    path: '/field/rfis',
    icon: FileQuestion,
    description: 'Raise and move the same ConstructionOS RFIs Procore writes and webhooks sync.',
    sprint: 'M08_FIELD_OPERATIONS',
    status: 'live',
    persona: 'Project Manager / Superintendent',
    purpose:
      'Field create and workflow on the COS Rfi record — one row for native COS and Procore POST/PATCH/webhooks.',
    capabilities: [
      'Native create on the shared COS Rfi row',
      'Optional auto-number matching Meyer job sequence',
      'Procore lineage from IntegrationVendorObject',
      'Status workflow that enqueues the existing Procore push',
    ],
    permission: PERMISSIONS.DOCUMENTS_RFIS_READ,
  },
  {
    id: 'field-tasks',
    label: 'Field Tasks',
    path: '/field/tasks',
    icon: ListChecks,
    description: 'The field punch list: what is open, what is blocked, and why.',
    sprint: 'M08_FIELD_OPERATIONS',
    status: 'live',
    persona: 'Superintendent / Field Worker',
    purpose: 'Assign, block with a recorded reason, and complete field work items.',
    capabilities: [
      'Assign to a person or a named crew',
      'Block with a required reason',
      'Priority and due date',
      '"Only mine" filter for the phone',
    ],
    permission: PERMISSIONS.FIELD_FIELD_TASKS_READ,
  },
  // ---- legacy group: Communications ----
  {
    id: 'inbox-triage',
    label: 'AI Email Overview',
    path: '/inbox',
    icon: Inbox,
    description: 'Construction-aware triage of company mail — what needs a person.',
    sprint: 'M10_INBOX_INTELLIGENCE',
    status: 'live',
    persona: 'Owner / Project Manager',
    purpose:
      'Collapse a day of email into the handful of threads that need a person, with everything else filed against its job, client, or vendor. Full personal Outlook management lives under Microsoft 365.',
    capabilities: [
      'Needs-human / auto-filed / ignored triage board, oldest-first where it matters',
      'Rules-first classification with an honest AI-or-rules source badge',
      'Approvals, rejections, money changes, and complaints are never auto-filed',
      'Draft replies with a deterministic fallback and a truthful send disclosure',
    ],
    permission: PERMISSIONS.INBOX_THREADS_READ,
  },
  // ---- legacy group: Portfolio ----
  {
    id: 'portfolio-showcases',
    label: 'Case Studies',
    path: '/portfolio',
    icon: Images,
    description: 'Turn a finished job into a one-page write-up for a prequalification package.',
    sprint: 'M12_PERSONA_DASHBOARDS',
    status: 'live',
    persona: 'Owner / Business Development',
    purpose:
      'Build the credibility artifact a company moving upmarket needs: scope, images, duration, and a value the team chose to disclose, on one page.',
    capabilities: [
      'Scope highlights written by the project team, never invented',
      'Narrative drafted with an honest AI-or-rules source badge',
      'Gallery restricted to documents on that project, showing which have files',
      'One-page PDF with the company mark, and an explicit publish gate',
    ],
    permission: PERMISSIONS.PORTFOLIO_SHOWCASES_READ,
  },
  // ---- legacy group: Compliance ----
  {
    id: 'compliance-board',
    label: 'Sub Compliance',
    path: '/compliance',
    icon: FileCheck2,
    description: 'Which subcontractors are on live jobs right now without provable insurance.',
    sprint: 'M09_SUB_COMPLIANCE',
    status: 'live',
    persona: 'Compliance Manager / Operations Manager',
    purpose:
      'See lapsed and expiring COIs against open work, then chase renewals automatically instead of remembering to.',
    capabilities: [
      '"Lapsed on an active job" flag on the sub, not buried in a folder',
      'Expiry calendar for COI, licence, and prequalification renewals',
      'Automated chase queue with the honest delivery outcome',
      'Unlinked certificates shown, never attributed by guesswork',
    ],
    permission: PERMISSIONS.COMPLIANCE_INSURANCE_CERTIFICATES_READ,
  },
  {
    id: 'contracts',
    label: 'Contracts',
    path: '/contracts',
    icon: FileSignature,
    description: 'Prime and subcontract agreements, and the obligations attached to them.',
    sprint: 'P8_CONTRACTS',
    status: 'live',
    persona: 'Project Executive / Project Manager / Owner',
    purpose:
      'Contract administration for a general contractor — obligation clauses (indemnification, retainage, insurance requirements) tracked open/satisfied/waived against a real due date, not a filing cabinet.',
    capabilities: [
      'Open and overdue obligation counts are a real, capped, derived tally — never a synthetic score',
      'Clauses tie to a due date and an owner (owner / GC / subcontractor)',
    ],
    permission: PERMISSIONS.CONTRACTS_READ,
  },
  // ---- legacy group: Platform ----
  {
    id: 'simulations',
    label: 'Simulations',
    path: '/simulations',
    icon: FlaskConical,
    description: 'Scenario engine, inputs, output charts and recommendation linkage.',
    sprint: 'S29_SIMULATION_ENGINE',
    status: 'live',
    persona: 'Executives / PMs',
    purpose: 'What-if scenario engine for schedule, cash, and crew decisions.',
    capabilities: [
      'Scenario templates and input forms',
      'Persisted simulation results',
      'Output charts',
      'Recommendation linkage',
    ],
    permission: PERMISSIONS.SIMULATIONS_READ,
  },
  {
    id: 'documents',
    label: 'Documents',
    path: '/documents',
    icon: Files,
    description: 'Project files, folders, uploads, approvals, and AI-assisted filing.',
    sprint: 'S26_DOCUMENTS_REPORTING',
    status: 'live',
    persona: 'All Users',
    purpose:
      'One place for the document library, project files, folders, version history, AI classification, and approval queues.',
    capabilities: [
      'Document library and versioning',
      'Folder tree with materialized paths',
      'Checksum-bound uploads and immutable version history',
      'Rules-first classification with needs-review queue',
      'AI document Q&A and approval workflows',
    ],
    permission: PERMISSIONS.PROJECTS_READ,
    anyPermissions: [PERMISSIONS.PROJECTS_READ, PERMISSIONS.DOCUMENTS_ASSETS_READ],
  },
  {
    id: 'reports',
    label: 'Reporting',
    path: '/reports',
    icon: BarChart3,
    description:
      'Create, edit, schedule, and manage saved reports, library templates, and export packs.',
    sprint: 'S26_DOCUMENTS_REPORTING',
    status: 'live',
    persona: 'Executives / Managers / Finance',
    purpose:
      'One place to create, edit, schedule, and manage every report type — saved definitions, board-grade library PDFs, and executive export packs.',
    capabilities: [
      'Create and edit saved report definitions',
      'Schedule recurring packets',
      'Board-grade library templates',
      'Executive and domain export packs',
    ],
    permission: PERMISSIONS.PROJECTS_EXPORT,
    anyPermissions: [
      PERMISSIONS.PROJECTS_EXPORT,
      PERMISSIONS.REPORTS_LIBRARY_READ,
      PERMISSIONS.REPORTS_EXPORT,
    ],
  },
  {
    id: 'integrations',
    label: 'Integrations',
    path: '/settings/integrations',
    icon: Plug,
    description:
      'Connect Procore and QuickBooks, inspect connector health, credentials, mappings, and logs.',
    sprint: 'S27_INTEGRATION_FRAMEWORK',
    status: 'live',
    persona: 'Admin',
    purpose: 'Connector health, sync mapping, logs, errors and credentials.',
    capabilities: [
      'Connector registry and health',
      'Sync mapping',
      'Logs and error triage',
      'Credential management',
    ],
    permission: PERMISSIONS.INTEGRATIONS_CONNECTIONS_READ,
  },
  {
    id: 'accounting-mappings',
    label: 'QuickBooks Mappings',
    path: '/banking/mappings',
    icon: Link2,
    description: 'One HELIOS record to one QuickBooks record, with a queue for anything ambiguous.',
    sprint: 'M13_ACCOUNTING_PAYROLL',
    status: 'live',
    persona: 'Finance / Admin',
    purpose:
      'Tie customers and vendors to their QuickBooks counterparts once, and resolve by hand anything the matcher refuses to decide.',
    capabilities: [
      'Auto-map by normalised name and email',
      'Confidence reported per proposal',
      'Conflict queue a human resolves',
    ],
    permission: PERMISSIONS.ACCOUNTING_ENTITY_LINKS_READ,
  },
  {
    id: 'accounting-sync',
    label: 'QuickBooks Sync',
    path: '/banking/sync',
    icon: Repeat,
    description: 'Run history, per-entity counts, and what each run refused to push.',
    sprint: 'M13_ACCOUNTING_PAYROLL',
    status: 'live',
    persona: 'Finance / Admin',
    purpose:
      'See what moved between HELIOS and QuickBooks, and what was deliberately held back for a human decision.',
    capabilities: [
      'Run history with per-entity counts',
      'Conflicts named on the run',
      'Idempotent re-runs',
    ],
    permission: PERMISSIONS.ACCOUNTING_SYNC_RUNS_READ,
  },
  {
    // Legacy banking entry remains routeable inside the unified Finance
    // workspace; QuickBooks book balances and Plaid activity stay separate.
    id: 'bank-accounts',
    label: 'Book Accounts',
    path: '/banking',
    icon: Landmark,
    description: 'QuickBooks book accounts with separate transaction source context and evidence.',
    sprint: 'M14_PLAID_BANKING',
    status: 'live',
    persona: 'Owner / Finance',
    purpose:
      'Inspect QuickBooks book balances in their source currency and original scope, then open associated transaction evidence without treating Plaid activity as an accounting balance.',
    capabilities: [
      'QuickBooks book balances with exact source identity, currency and observation date',
      'Separate Plaid transaction descriptions, merchants, bank categories and status',
      'Source account association review without merging provider balances',
      'Interactive account and transaction evidence inspectors',
    ],
    permission: PERMISSIONS.BANKING_ACCOUNTS_READ,
    railAnyPermissions: [
      PERMISSIONS.BANKING_ACCOUNTS_READ,
      PERMISSIONS.BANKING_TRANSACTIONS_READ,
      PERMISSIONS.BANKING_RECONCILIATION_READ,
      PERMISSIONS.ACCOUNTING_ENTITY_LINKS_READ,
      PERMISSIONS.ACCOUNTING_SYNC_RUNS_READ,
    ],
    matchPrefix: true,
  },
  {
    id: 'bank-transactions',
    label: 'Bank Register',
    path: '/banking/transactions',
    icon: Receipt,
    description: 'Every transaction on every account, and what the platform believes it is.',
    sprint: 'M14_PLAID_BANKING',
    status: 'live',
    persona: 'Owner / Finance',
    purpose:
      'Search and filter the whole register, including the transfers and reversals that are excluded from every total.',
    capabilities: [
      'Filter by account, direction, match state and date',
      'Reversed transactions kept and marked, never deleted',
      'Internal transfers hidden by default and never counted as spend',
    ],
    permission: PERMISSIONS.BANKING_TRANSACTIONS_READ,
  },
  {
    id: 'takeoff',
    label: 'Takeoff',
    path: '/takeoff',
    icon: Ruler,
    description:
      'Measure a drawing set into a materials punch list, with every quantity traceable to the sheet it came from.',
    sprint: 'M16_TAKEOFF_ENGINE',
    status: 'live',
    persona: 'Chief Estimator / Estimator',
    purpose:
      'Turn an issued drawing set into a priceable materials list, and see plainly what the engine could not measure so an estimator fills it rather than a bid absorbing it.',
    capabilities: [
      'Schedule tables and vector geometry measured; scans refused rather than guessed',
      'Every quantity carries its page, its derivation and its confidence',
      'Gaps queued for the estimator — never defaulted, interpolated or averaged',
      'Waste applied as a named, visible step beside the measured quantity',
    ],
    permission: PERMISSIONS.TAKEOFF_DRAWING_SETS_READ,
  },
  {
    id: 'competitors',
    label: 'Competitors',
    path: '/estimating/competitors',
    icon: Swords,
    description: 'Who you are actually bidding against, and the real win/loss record.',
    sprint: 'P9_COMPETITOR_INTEL',
    status: 'live',
    persona: 'Chief Estimator / Preconstruction Manager',
    purpose:
      'A named competitor list and a real win/loss tally tied to actual leads and opportunities — deliberately no synthetic threat score or fabricated trend line.',
    capabilities: [
      'Win/loss counts are computed from real, tagged Lead/Opportunity records, never invented',
      'No hash-derived or simulated "threat score" — if there is no real signal, nothing is shown',
    ],
    permission: PERMISSIONS.COMPETITOR_INTEL_COMPETITORS_READ,
  },
  {
    id: 'competitor-signals',
    label: 'Signals',
    path: '/estimating/competitors/signals',
    icon: Radar,
    description:
      'Observed moves: bid results, hires, expansions, acquisitions — each with its source.',
    sprint: 'P13_COMPETITOR_INTEL',
    status: 'live',
    persona: 'Chief Estimator / Preconstruction Manager',
    permission: PERMISSIONS.COMPETITOR_INTEL_SIGNALS_READ,
  },
  {
    id: 'competitor-news',
    label: 'News',
    path: '/estimating/competitors/news',
    icon: Newspaper,
    description: 'Published articles about competitors and the market, always attributed.',
    sprint: 'P13_COMPETITOR_INTEL',
    status: 'live',
    persona: 'Chief Estimator',
    permission: PERMISSIONS.COMPETITOR_INTEL_SIGNALS_READ,
  },
  {
    id: 'competitor-alerts',
    label: 'Alerts',
    path: '/estimating/competitors/alerts',
    icon: Siren,
    description: 'What someone decided the team should act on, and whether it was.',
    sprint: 'P13_COMPETITOR_INTEL',
    status: 'live',
    persona: 'Chief Estimator / Owner',
    permission: PERMISSIONS.COMPETITOR_INTEL_ALERTS_READ,
  },
  {
    id: 'client-contact-detail',
    label: 'Contact',
    path: '/crm/clients/contacts/:id',
    icon: ContactRound,
    description: 'One contact: interaction history, notes, key dates, files and engagement.',
    sprint: 'P13_CRM_DEPTH',
    status: 'live',
    persona: 'Business Development / Project Executive',
    permission: PERMISSIONS.CRM_CLIENT_CONTACTS_READ,
  },
  {
    id: 'comping',
    label: 'Comping',
    path: '/comping',
    icon: Coins,
    description:
      'Price a materials punch list against every quote and cost-book price on file, and record why each vendor won.',
    sprint: 'M17_COMPING_LABOR',
    status: 'live',
    persona: 'Chief Estimator / Estimator',
    purpose:
      'Turn a measured punch list into a priced one, with a selection an estimator can argue with rather than a number they have to accept.',
    capabilities: [
      'Landed cost — unit price plus quoted freight and tax, never a headline price',
      'A vendor who cannot deliver by the need-by date is ruled out, not ranked lower',
      'Reliability from this company’s own delivery record; no history means no score, never a zero',
      'A lapsed certificate of insurance blocks selection and says so',
      'Every selection names the runner-up and the difference; every override records a reason',
    ],
    permission: PERMISSIONS.COMPING_RUNS_READ,
  },
  {
    id: 'labor-plan',
    label: 'Labour Plan',
    path: '/labor-plan',
    icon: HardHat,
    description:
      'Hours from production rates, adjusted by named factors, staffed by people who hold the skill and the ticket.',
    sprint: 'M17_COMPING_LABOR',
    status: 'live',
    persona: 'Chief Estimator / Superintendent',
    purpose:
      'Establish how long the work takes and who can actually do it, so a bid rests on a crew that exists rather than on a headcount.',
    capabilities: [
      'Hours = quantity ÷ production rate, with the rate’s basis quoted on every task',
      'Productivity factors are named, valued and defended one at a time — never one multiplier',
      'Availability means schedule commitment on another job, never presence or chat status',
      'An expired certificate disqualifies; a missing skill disqualifies; both say why',
      'Burden itemised — payroll tax, workers’ comp, liability, overhead — beside the base rate',
    ],
    permission: PERMISSIONS.LABOR_PLANS_READ,
  },
  {
    id: 'estimating-equipment',
    label: 'Equipment',
    path: '/estimating/equipment',
    icon: Wrench,
    description: 'Reviewed equipment costs and feasibility for the planned job window.',
    sprint: 'M17_COMPING_LABOR',
    status: 'live',
    persona: 'Chief Estimator',
    purpose:
      'Cost machinery and tools against explicit rates, duration, transport, availability and source evidence, with separate human review and approval.',
    capabilities: [
      'Source-bound owned and rental equipment plan lines',
      'Exact cost totals with explicit unavailable inputs',
      'Window, capacity, delivery and certificate checks',
      'Reviewed and approved revisions with preserved history',
    ],
    permission: PERMISSIONS.ESTIMATING_EQUIPMENT_PLANS_READ,
    anyPermissions: [
      PERMISSIONS.ESTIMATING_EQUIPMENT_PLANS_READ,
      PERMISSIONS.ESTIMATING_COST_ITEMS_READ,
    ],
  },
  {
    id: 'hr',
    label: 'HR',
    path: '/hr',
    icon: Users,
    description:
      'The employment lifecycle: employees, organization, onboarding, compensation, time, performance, documents, compliance and imports.',
    sprint: 'M18_HR',
    status: 'live',
    persona: 'HR Manager / Owner',
    purpose:
      'Own the whole of an employment relationship in one place, rather than reading a person across labour, payroll and compliance screens that each hold a fragment.',
    capabilities: [
      'Roster shows employment, not pay — compensation is a separate grant',
      'Termination is its own action, not a status field, and closes the open pay row',
      'Onboarding approval is what makes employment active; nobody joins by filling in a form',
      'Signatures are pinned to the document version signed, so a later revision does not inherit consent',
      'Certification currency is evaluated against today, never a cached status',
    ],
    permission: PERMISSIONS.HR_EMPLOYEES_READ,
    railAnyPermissions: [
      PERMISSIONS.HR_EMPLOYEES_READ,
      PERMISSIONS.HR_DOCUMENTS_READ,
      PERMISSIONS.HR_TIME_ENTRIES_READ,
      PERMISSIONS.HR_IMPORTS_READ,
    ],
    matchPrefix: true,
  },
  {
    id: 'time-automation',
    label: 'Time & Cost Automation',
    path: '/time-automation',
    icon: SlidersHorizontal,
    description:
      'Field and office time that captures itself, job provisioning, and the controls that keep every automation in dry-run until an Owner says otherwise.',
    sprint: 'P17_TIME_AUTOMATION',
    status: 'live',
    persona: 'Owner / President / HR Manager',
    purpose:
      'People confirm their time; they do not create it. Switch each automation on deliberately, see what it would do in dry-run, and take it live only when it is ready.',
    capabilities: [
      'Every workstream is off, and in dry-run, until an Owner turns it on',
      'Going live is its own permission and writes a reason to the audit log',
      'Anything that observes an employee is blocked until the section 52-c notice is acknowledged',
    ],
    permission: PERMISSIONS.TIME_AUTOMATION_CONTROLS_READ,
    railAnyPermissions: [
      PERMISSIONS.TIME_AUTOMATION_CONTROLS_READ,
      PERMISSIONS.TIME_AUTOMATION_MONITORING_READ,
    ],
    matchPrefix: true,
  },
  {
    id: 'time-automation-monitoring',
    label: 'Monitoring Notice',
    path: '/time-automation/monitoring',
    icon: FileSignature,
    description:
      'The NY Civil Rights Law section 52-c electronic-monitoring notice, who has acknowledged it, and who is blocked.',
    sprint: 'P17_TIME_AUTOMATION',
    status: 'live',
    persona: 'HR Manager / Owner / Counsel',
    purpose:
      'Keep the versioned notice, the signed acknowledgments and the posting record in one place, and see exactly which employees are blocked from location capture and activity signals.',
    capabilities: [
      'Published notices are immutable; a revision must be acknowledged again',
      'Counsel review and conspicuous posting are recorded against the version',
      'Coverage lists blocked employees first',
    ],
    permission: PERMISSIONS.TIME_AUTOMATION_MONITORING_READ,
  },
  {
    id: 'time-automation-provisioning',
    label: 'Job Provisioning',
    path: '/time-automation/provisioning',
    icon: Settings2,
    description:
      'Job numbering, the SharePoint folder template and the Microsoft 365 connection that sets a new job up in one action.',
    sprint: 'P17_TIME_AUTOMATION',
    status: 'live',
    persona: 'Owner / President / Integration Admin',
    purpose:
      'Decide how a job is numbered and where its Teams channel and SharePoint folder are created, and connect the Microsoft 365 application that does it.',
    capabilities: [
      'A job number is issued once, in the configured format, and never changes',
      'The Microsoft 365 client secret is encrypted and never shown again',
      'Nothing here switches automation on — that is the control centre',
    ],
    anyPermissions: [
      PERMISSIONS.TIME_AUTOMATION_PROVISIONING_READ,
      PERMISSIONS.TIME_AUTOMATION_INTEGRATIONS_READ,
    ],
  },
  {
    id: 'time-automation-field-time',
    label: 'Field Time',
    path: '/time-automation/field-time',
    icon: Clock,
    description:
      'Punches from Microsoft Shifts, pre-filled from the crew schedule, with the exceptions a person has to resolve and the foreman split.',
    sprint: 'P17_TIME_AUTOMATION',
    status: 'live',
    persona: 'Foreman / Project Manager / HR Manager',
    purpose:
      'See every punch, fix the open and flagged ones, and split a day across jobs while it happens — without anyone retyping hours.',
    capabilities: [
      'A missed clock-out is flagged to the project manager, never closed for you',
      'A split made after the fact is flagged retroactive and needs a second person to approve',
      'Crew defaults pre-fill the job, cost code and wage classification',
    ],
    permission: PERMISSIONS.TIME_AUTOMATION_FIELD_TIME_READ,
  },
  {
    id: 'time-automation-reconciliation',
    label: 'ClockShark Reconciliation',
    path: '/time-automation/reconciliation',
    icon: Scale,
    description:
      'ClockShark and Shifts side by side, every difference explained, and the Owner-approved cutover that retires ClockShark.',
    sprint: 'P17_TIME_AUTOMATION',
    status: 'live',
    persona: 'Owner / Payroll / HR Manager',
    purpose:
      'Run both systems in parallel and retire ClockShark only when two consecutive full payroll cycles agree within the tolerance.',
    capabilities: [
      'Differences listed largest first with a written explanation',
      'Cutover needs two clean cycles and an Owner approval',
      'Rolling back re-enables ClockShark and keeps everything captured',
    ],
    permission: PERMISSIONS.TIME_AUTOMATION_FIELD_TIME_READ,
  },
  {
    id: 'time-automation-field-time-setup',
    label: 'Field Time Setup',
    path: '/time-automation/field-time-setup',
    icon: Settings2,
    description:
      'Alert and cutoff thresholds, the Microsoft Shifts connection, the Microsoft 365 user map and payroll-period locks.',
    sprint: 'P17_TIME_AUTOMATION',
    status: 'live',
    persona: 'Owner / HR Manager / Payroll',
    purpose:
      'Set the business rules the capture runs on, connect Shifts, say who is who in Microsoft 365, and lock a payroll period once it is clean.',
    capabilities: [
      'Every threshold is shown as unratified until saved',
      'A locked period refuses punches from every source',
      'Releasing a lock needs a written reason',
    ],
    permission: PERMISSIONS.TIME_AUTOMATION_FIELD_TIME_READ,
  },
  {
    id: 'insurance',
    label: 'Insurance',
    path: '/insurance',
    icon: ShieldPlus,
    description:
      "The company's own insurance program: policies, claims, premiums, coverage and carriers.",
    sprint: 'P12_INSURANCE',
    status: 'live',
    persona: 'CFO / Controller / Safety Manager / Owner',
    purpose:
      'Know what the business is covered for, what that cover costs, when it renews, and what has been claimed against it — separate from the subcontractor certificates on the compliance board.',
    capabilities: [
      'Policies carry real limits, deductibles and renewal dates — expiry is computed, never typed',
      'Claims follow an enforced lifecycle and paid can never exceed approved, nor approved exceed claimed',
      'Premium schedule reconciles to what has actually been paid',
    ],
    permission: PERMISSIONS.INSURANCE_POLICIES_READ,
    railAnyPermissions: [
      PERMISSIONS.INSURANCE_POLICIES_READ,
      PERMISSIONS.INSURANCE_CLAIMS_READ,
      PERMISSIONS.INSURANCE_PREMIUMS_READ,
    ],
    matchPrefix: true,
  },
  {
    id: 'insurance-alerts',
    label: 'Alerts',
    path: '/insurance/alerts',
    icon: BellRing,
    description:
      'Cover the business is missing, policies about to lapse, late premiums and coverage-analysis findings.',
    sprint: 'P12_INSURANCE',
    status: 'live',
    persona: 'CFO / Controller / Owner',
    permission: PERMISSIONS.INSURANCE_POLICIES_READ,
  },
  {
    id: 'insurance-intake',
    label: 'Upload policies',
    path: '/insurance/intake',
    icon: FileUp,
    description:
      'Upload one policy or many: the engine reads each document, fills in the register and stores the document with its policy.',
    sprint: 'P12_INSURANCE',
    status: 'live',
    persona: 'CFO / Controller',
    permission: PERMISSIONS.INSURANCE_POLICIES_READ,
  },
  {
    id: 'insurance-policies',
    label: 'Policies',
    path: '/insurance/policies',
    icon: FileBadge,
    description: 'Every policy the company holds, with limits, deductibles and renewal dates.',
    sprint: 'P12_INSURANCE',
    status: 'live',
    persona: 'CFO / Controller',
    permission: PERMISSIONS.INSURANCE_POLICIES_READ,
    matchPrefix: true,
  },
  {
    id: 'insurance-claims',
    label: 'Claims',
    path: '/insurance/claims',
    icon: FileCheck2,
    description: 'Claims the company has made against its own policies.',
    sprint: 'P12_INSURANCE',
    status: 'live',
    persona: 'CFO / Safety Manager',
    permission: PERMISSIONS.INSURANCE_CLAIMS_READ,
  },
  {
    id: 'insurance-premiums',
    label: 'Premiums',
    path: '/insurance/premiums',
    icon: Percent,
    description: 'The premium payment schedule and what has been paid.',
    sprint: 'P12_INSURANCE',
    status: 'live',
    persona: 'Controller',
    permission: PERMISSIONS.INSURANCE_PREMIUMS_READ,
  },
  {
    id: 'insurance-coverage',
    label: 'Coverage & Risk',
    path: '/insurance/coverage',
    icon: ShieldCheck,
    description: 'Named exposures under each policy, their limits and recorded risk level.',
    sprint: 'P12_INSURANCE',
    status: 'live',
    persona: 'Safety Manager / COO',
    permission: PERMISSIONS.INSURANCE_POLICIES_READ,
  },
  {
    id: 'insurance-carriers',
    label: 'Carriers',
    path: '/insurance/carriers',
    icon: Handshake,
    description: 'Carriers, agents, brokers and adjusters.',
    sprint: 'P12_INSURANCE',
    status: 'live',
    persona: 'CFO / Controller',
    permission: PERMISSIONS.INSURANCE_POLICIES_READ,
  },
  {
    id: 'insurance-market',
    label: 'Market',
    path: '/insurance/market',
    icon: Scale,
    description:
      'Compare the cover you have with quotes from other local brokers and carriers — like for like — and send them a ready-made submission.',
    sprint: 'P12_INSURANCE',
    status: 'live',
    persona: 'CFO / Controller / Owner',
    permission: PERMISSIONS.INSURANCE_POLICIES_READ,
  },
  {
    id: 'insurance-documents',
    label: 'Documents',
    path: '/insurance/documents',
    icon: FileText,
    description: 'Policy documents, declarations, endorsements and claim correspondence.',
    sprint: 'P12_INSURANCE',
    status: 'live',
    persona: 'Controller',
    permission: PERMISSIONS.INSURANCE_POLICIES_READ,
  },
  {
    id: 'insurance-profile',
    label: 'Business profile',
    path: '/insurance/profile',
    icon: ClipboardList,
    description:
      'What the business does, and the cover it therefore needs — checked against the register.',
    sprint: 'P12_INSURANCE',
    status: 'live',
    persona: 'CFO / Controller / Owner',
    permission: PERMISSIONS.INSURANCE_POLICIES_READ,
  },
  {
    id: 'insurance-coverage-gaps',
    label: 'Coverage gaps',
    path: '/insurance/coverage-gaps',
    icon: ShieldAlert,
    description: 'AI analysis of the whole insurance program: gaps, overlaps and payout scenarios.',
    sprint: 'P12_INSURANCE',
    status: 'live',
    persona: 'CFO / Controller',
    permission: PERMISSIONS.INSURANCE_POLICIES_READ,
  },
  {
    id: 'marketing',
    label: 'Marketing',
    path: '/marketing',
    icon: Megaphone,
    description:
      'Owned-channel marketing: fact-checked posts, second-person approval, and publishing to Instagram, Facebook and the website feed.',
    sprint: 'P15_MARKETING',
    status: 'live',
    persona: 'CEO / President / Client Executive',
    purpose:
      'Say things publicly that are true, on brand, and approved by someone other than the author — then prove where each post went.',
    capabilities: [
      'Every factual claim is grounded against a named source before approval',
      'Approval needs a separate permission and a second person; autopilot only drafts',
      'A publication Meta did not confirm stops for reconciliation instead of posting twice',
    ],
    permission: PERMISSIONS.MARKETING_CONTENT_READ,
    railAnyPermissions: [PERMISSIONS.MARKETING_CONTENT_READ, PERMISSIONS.MARKETING_ENGAGEMENT_READ],
    matchPrefix: true,
  },
  {
    id: 'marketing-content',
    label: 'Content',
    path: '/marketing/content',
    icon: FileText,
    description: 'Drafts, items awaiting approval, and everything published.',
    sprint: 'P15_MARKETING',
    status: 'live',
    persona: 'Client Executive / Project Executive',
    permission: PERMISSIONS.MARKETING_CONTENT_READ,
    matchPrefix: true,
  },
  {
    id: 'marketing-autopilot',
    label: 'Autopilot',
    path: '/marketing/autopilot',
    icon: Sparkles,
    description: 'Draft a post from a project record. It never approves or publishes.',
    sprint: 'P15_MARKETING',
    status: 'live',
    persona: 'Client Executive',
    permission: PERMISSIONS.MARKETING_CONTENT_CREATE,
  },
  {
    id: 'marketing-channels',
    label: 'Channels',
    path: '/marketing/channels',
    icon: Plug,
    description: 'The Meta connection with its explicit Page picker, and the website feed.',
    sprint: 'P15_MARKETING',
    status: 'live',
    persona: 'CEO / President',
    permission: PERMISSIONS.MARKETING_CONTENT_READ,
  },
  {
    id: 'marketing-engagement',
    label: 'Command Center',
    path: '/marketing/engagement',
    icon: MessagesSquare,
    description: 'The Facebook Page and Instagram account: posts, comments and replies.',
    sprint: 'P15_MARKETING',
    status: 'live',
    persona: 'Client Executive',
    permission: PERMISSIONS.MARKETING_ENGAGEMENT_READ,
  },
  {
    id: 'marketing-brand-studio',
    label: 'Brand Studio',
    path: '/marketing/brand-studio',
    icon: Palette,
    description:
      'Email signatures, digital cards with QR, meeting backgrounds, logos and photo filters.',
    sprint: 'P15_MARKETING',
    status: 'live',
    persona: 'Everyone in a client-facing role',
    permission: PERMISSIONS.MARKETING_CONTENT_READ,
  },
  {
    id: 'it-estate',
    label: 'Technology',
    path: '/technology',
    icon: MonitorCog,
    description:
      'The IT estate: hardware, software subscriptions, security cameras, and the live Intune fleet.',
    sprint: 'P14_TECHNOLOGY',
    status: 'live',
    persona: 'Integration Admin / COO / Office Manager',
    purpose:
      'Know what hardware the company owns and who is holding it, what software it pays for and who holds each seat, and what the cameras cover — with the live Intune and Microsoft 365 view alongside for reconciliation.',
    capabilities: [
      'Hardware custody is a log, not a column — an asset can never be issued to two people at once',
      'Seats are counted from who holds them, never typed in, and cannot exceed what was bought',
      'The Intune view is live or says why it is not; it never shows an empty grid for a disconnected tenant',
    ],
    permission: PERMISSIONS.TECHNOLOGY_ASSETS_READ,
    // Every grant a Technology tab admits, so the hub shell lets its holder
    // reach that tab (Devices / Licences / Directory / Tenant need devices
    // read; Import needs import manage).
    railAnyPermissions: [
      PERMISSIONS.TECHNOLOGY_ASSETS_READ,
      PERMISSIONS.TECHNOLOGY_SOFTWARE_READ,
      PERMISSIONS.TECHNOLOGY_CAMERAS_READ,
      PERMISSIONS.TECHNOLOGY_DEVICES_READ,
      PERMISSIONS.TECHNOLOGY_IMPORT_MANAGE,
    ],
    matchPrefix: true,
  },
  {
    id: 'it-hardware',
    label: 'Hardware',
    path: '/technology/hardware',
    icon: Laptop,
    description:
      'Laptops, desktops, field rugged and network gear — what it cost, and who is holding it.',
    sprint: 'P14_TECHNOLOGY',
    status: 'live',
    persona: 'Integration Admin / HR Manager',
    permission: PERMISSIONS.TECHNOLOGY_ASSETS_READ,
    matchPrefix: true,
  },
  {
    id: 'it-software',
    label: 'Software',
    path: '/technology/software',
    icon: HardDrive,
    description:
      'Subscriptions, licences and support contracts with annualised cost and real seat counts.',
    sprint: 'P14_TECHNOLOGY',
    status: 'live',
    persona: 'Integration Admin / CFO',
    permission: PERMISSIONS.TECHNOLOGY_SOFTWARE_READ,
    matchPrefix: true,
  },
  {
    id: 'it-cameras',
    label: 'Cameras',
    path: '/technology/cameras',
    icon: Video,
    description:
      'Security camera coverage, recorders and footage retention across offices, yards and jobsites.',
    sprint: 'P14_TECHNOLOGY',
    status: 'live',
    persona: 'Integration Admin / Safety Manager',
    permission: PERMISSIONS.TECHNOLOGY_CAMERAS_READ,
    matchPrefix: true,
  },
  {
    id: 'it-devices',
    label: 'Managed devices',
    path: '/technology/devices',
    icon: CloudCog,
    description:
      'Live Intune compliance and Microsoft 365 licence counts, reconciled against the register.',
    sprint: 'P14_TECHNOLOGY',
    status: 'live',
    persona: 'Integration Admin',
    permission: PERMISSIONS.TECHNOLOGY_DEVICES_READ,
  },
  {
    id: 'it-licenses',
    label: 'Licenses',
    path: '/technology/licenses',
    icon: BadgeCheck,
    description:
      'Microsoft 365 licences from the scheduled directory sync: seats bought, assigned, who holds each, and the findings and security coverage derived from them.',
    sprint: 'P14_TECHNOLOGY',
    status: 'live',
    persona: 'Integration Admin / CFO',
    // Licence seats are software spend: the software register's readers
    // (CFO / Controller / Finance Manager) open it; device managers too.
    permission: PERMISSIONS.TECHNOLOGY_SOFTWARE_READ,
    anyPermissions: [PERMISSIONS.TECHNOLOGY_SOFTWARE_READ, PERMISSIONS.TECHNOLOGY_DEVICES_READ],
  },
  {
    id: 'it-directory',
    label: 'Directory & Roles',
    path: '/technology/directory',
    icon: UserCog,
    description: 'Entra ID directory roles and their members, privileged roles first.',
    sprint: 'P14_TECHNOLOGY',
    status: 'live',
    persona: 'Integration Admin',
    permission: PERMISSIONS.TECHNOLOGY_DEVICES_READ,
  },
  {
    id: 'it-network',
    label: 'Network',
    path: '/technology/network',
    icon: Network,
    description:
      'Firewalls, switches, routers, access points and storage with addressing and stored keys.',
    sprint: 'P14_TECHNOLOGY',
    status: 'live',
    persona: 'Integration Admin',
    permission: PERMISSIONS.TECHNOLOGY_ASSETS_READ,
  },
  {
    id: 'it-tenant-config',
    label: 'Tenant Config',
    path: '/technology/tenant',
    icon: Settings,
    description:
      'The Microsoft 365 connection, sync status per section, domains, security defaults and Conditional Access.',
    sprint: 'P14_TECHNOLOGY',
    status: 'live',
    persona: 'Integration Admin',
    permission: PERMISSIONS.TECHNOLOGY_DEVICES_READ,
  },
  {
    id: 'it-import',
    label: 'Import',
    path: '/technology/import',
    icon: FileUp,
    description:
      'Import a network runbook (PDF) or setup checklist (XLSX): preview, then commit in one step.',
    sprint: 'P14_TECHNOLOGY',
    status: 'live',
    persona: 'Integration Admin',
    permission: PERMISSIONS.TECHNOLOGY_IMPORT_MANAGE,
  },
  {
    id: 'debt',
    label: 'Credit Facilities',
    path: '/credit-facilities',
    icon: Banknote,
    description:
      'Bank lines, credit cards, term facilities and vendor running accounts with source evidence, capacity and payment planning.',
    sprint: 'P12_DEBT',
    status: 'live',
    persona: 'CFO / Controller / Owner',
    purpose:
      'One view of what the business owes, to whom, at what rate, and what is due next — with every balance movement backed by a payment row rather than a typed number.',
    capabilities: [
      'Balance after every payment is computed from the principal split, never accepted from the client',
      'Principal + interest + fees must equal the payment amount',
      'Upcoming payments and maturities are derived from real schedule fields',
    ],
    permission: PERMISSIONS.DEBT_DEBTS_READ,
    railAnyPermissions: [PERMISSIONS.DEBT_DEBTS_READ, PERMISSIONS.DEBT_PAYMENTS_READ],
    matchPrefix: true,
  },
  {
    id: 'sales-pipeline',
    label: 'Sales Pipeline',
    path: '/sales',
    icon: Target,
    description:
      'Business development before the bid: prospects, touchpoints, tasks and introductions.',
    sprint: 'P12_SALES',
    status: 'live',
    persona: 'Owner / President / Chief Estimator',
    purpose:
      'Work the relationship with an owner or developer before they put a job out — the stage that precedes the estimating lead funnel, and which feeds it.',
    capabilities: [
      'A prospect converts into a real Lead rather than being counted twice',
      'Every touchpoint is something that happened, recorded by the person who did it',
      'Win and loss are dated with a reason, so the pipeline can be reviewed honestly',
    ],
    permission: PERMISSIONS.SALES_PROSPECTS_READ,
    matchPrefix: true,
  },
  {
    id: 'sales-prospects',
    label: 'Prospects',
    path: '/sales/prospects',
    icon: UserSearch,
    description: 'Every prospect, its stage, owner and expected value.',
    sprint: 'P12_SALES',
    status: 'live',
    persona: 'Chief Estimator / Owner',
    permission: PERMISSIONS.SALES_PROSPECTS_READ,
    matchPrefix: true,
  },
  {
    id: 'sales-activity',
    label: 'Activity',
    path: '/sales/activity',
    icon: CalendarCheck,
    description: 'Touchpoints and open tasks across the whole pipeline.',
    sprint: 'P12_SALES',
    status: 'live',
    persona: 'Chief Estimator',
    permission: PERMISSIONS.SALES_PROSPECTS_READ,
  },
  {
    id: 'sales-reports',
    label: 'Reports',
    path: '/sales/reports',
    icon: BarChart3,
    description: 'Pipeline value by stage, conversion, and win/loss reasons.',
    sprint: 'P12_SALES',
    status: 'live',
    persona: 'Owner / President',
    permission: PERMISSIONS.SALES_PROSPECTS_READ,
  },
  {
    id: 'general-ledger',
    label: 'General Ledger',
    path: '/general-ledger',
    icon: Landmark,
    description: 'Chart of accounts, journal entries, and a real trial balance.',
    sprint: 'P8_GENERAL_LEDGER',
    status: 'live',
    persona: 'CFO / Controller / Finance Manager',
    purpose:
      'A native book of record for a tenant with no QuickBooks connection, or that wants HELIOS to be the source of truth — post a manual journal entry and pull a trial balance without leaving the platform.',
    capabilities: [
      'Every journal entry must balance — debits equal credits, enforced server-side, or it is rejected',
      'Posting is a separate, harder-to-reverse action from drafting — its own permission',
      'Decimal money throughout, never a floating-point number',
    ],
    permission: PERMISSIONS.FINANCE_LEDGER_ACCOUNTS_READ,
  },
  {
    id: 'legal-entities',
    label: 'Entities',
    path: '/legal-entities',
    icon: Building2,
    description:
      'The corporate/JV entities behind the business: owners, agreements, filings, tax returns, board meetings and the document vault.',
    sprint: 'P8_LEGAL_ENTITIES',
    status: 'live',
    persona: 'CEO / President / CFO / Controller',
    purpose:
      'One record per legal entity with everything the Secretary of State, the IRS and the board expect to exist — and a compliance score that only moves when a real deadline, document or registered agent does.',
    capabilities: [
      'Ownership percentages are validated against the entity total, never left to silently exceed 100%',
      'Annual and tax filings carry real due dates; overdue is computed, never typed',
      'EIN held encrypted with a last-4; a full reveal is a separate, audited permission',
      'Entity hierarchy (parent/child) for holding-company structures',
    ],
    permission: PERMISSIONS.LEGAL_ENTITIES_READ,
    matchPrefix: true,
  },
  {
    id: 'legal-overview',
    label: 'Legal',
    path: '/legal',
    icon: Gavel,
    description: 'Matters, counsel, spend, deadlines and exposure — the legal command center.',
    sprint: 'P8_LEGAL',
    status: 'live',
    persona: 'CEO / President / CFO / COO',
    purpose:
      'The internal legal back office: open matters and their exposure, outside counsel and retainer balances, the invoice ledger, the deadline calendar and a transparent compliance score.',
    capabilities: [
      'Every KPI is arithmetic over real matters, expenses and retainer rows — nothing is a placeholder',
      'Retainer balances are an append-only ledger; every deduction is a row someone recorded',
      'Compliance score deducts fixed points per overdue deadline, overdue invoice and high-risk matter',
    ],
    permission: PERMISSIONS.LEGAL_MATTERS_READ,
    railAnyPermissions: [
      PERMISSIONS.LEGAL_MATTERS_READ,
      PERMISSIONS.LEGAL_COUNSEL_READ,
      PERMISSIONS.LEGAL_EXPENSES_READ,
      PERMISSIONS.LEGAL_ENTITIES_READ,
      PERMISSIONS.CONTRACTS_READ,
    ],
  },
  {
    id: 'legal-matters',
    label: 'Matters',
    path: '/legal/matters',
    icon: Scale,
    description:
      'Litigation, contract, compliance, employment and IP matters with counsel, documents, expenses and deadlines.',
    sprint: 'P8_LEGAL',
    status: 'live',
    persona: 'CEO / President / COO',
    permission: PERMISSIONS.LEGAL_MATTERS_READ,
    matchPrefix: true,
  },
  {
    id: 'legal-counsel',
    label: 'Counsel',
    path: '/legal/counsel',
    icon: Contact,
    description: 'Law firms, attorneys, retainer ledgers and counsel invoices.',
    sprint: 'P8_LEGAL',
    status: 'live',
    persona: 'CEO / President / CFO',
    permission: PERMISSIONS.LEGAL_COUNSEL_READ,
    matchPrefix: true,
  },
  {
    id: 'legal-invoices',
    label: 'Invoices',
    path: '/legal/invoices',
    icon: Receipt,
    description: 'The legal expense and invoice ledger — pending, paid and overdue.',
    sprint: 'P8_LEGAL',
    status: 'live',
    persona: 'CFO / Controller',
    permission: PERMISSIONS.LEGAL_EXPENSES_READ,
  },
  {
    id: 'legal-calendar',
    label: 'Calendar',
    path: '/legal/calendar',
    icon: CalendarDays,
    description:
      'Court dates, filing deadlines, hearings and expected resolutions across every open matter.',
    sprint: 'P8_LEGAL',
    status: 'live',
    persona: 'CEO / President / COO',
    permission: PERMISSIONS.LEGAL_MATTERS_READ,
  },
  {
    id: 'legal-risk',
    label: 'Risk & Compliance',
    path: '/legal/risk',
    icon: ShieldAlert,
    description: 'Exposure by risk level, regulatory matters and the transparent compliance score.',
    sprint: 'P8_LEGAL',
    status: 'live',
    persona: 'CEO / President / COO',
    permission: PERMISSIONS.LEGAL_MATTERS_READ,
  },
  {
    id: 'legal-reports',
    label: 'Reports',
    path: '/legal/reports',
    icon: BarChart3,
    description: 'Spend by month, matters by type and top counsel by spend.',
    sprint: 'P8_LEGAL',
    status: 'live',
    persona: 'CEO / President / CFO',
    permission: PERMISSIONS.LEGAL_MATTERS_READ,
  },
  {
    id: 'benefits',
    label: 'Benefits',
    path: '/benefits',
    icon: HeartPulse,
    description:
      'Plans, enrollment, vendors, costs, claims, compliance and documents — the benefits command center.',
    sprint: 'P8_BENEFITS',
    status: 'live',
    persona: 'HR Manager / Owner',
    purpose:
      'One place for what benefit plans exist, who is enrolled in them, what they cost the company and the employee, which claims are open and when everything renews.',
    capabilities: [
      'Enrolled headcount, enrollment rate and cost per enrolled employee are counted from real enrollment rows',
      'Claims follow an enforced lifecycle; paid can never exceed allowed, allowed never exceed billed',
      'Compliance is the required-document check plus the written policy library — no invented benchmark',
    ],
    permission: PERMISSIONS.BENEFITS_PLANS_READ,
    railAnyPermissions: [
      PERMISSIONS.BENEFITS_PLANS_READ,
      PERMISSIONS.BENEFITS_ENROLLMENTS_READ,
      PERMISSIONS.BENEFITS_CLAIMS_READ,
    ],
    matchPrefix: true,
  },
  {
    id: 'benefits-plans',
    label: 'Plans',
    path: '/benefits/plans',
    icon: Stethoscope,
    description:
      'The plan library: carrier, group and policy numbers, portals, costs and coverage.',
    sprint: 'P8_BENEFITS',
    status: 'live',
    persona: 'HR Manager',
    permission: PERMISSIONS.BENEFITS_PLANS_READ,
    matchPrefix: true,
  },
  {
    id: 'benefits-enrollment',
    label: 'Enrollment',
    path: '/benefits/enrollment',
    icon: Users,
    description:
      'Who is enrolled in which plan, at which coverage tier, and what each side contributes.',
    sprint: 'P8_BENEFITS',
    status: 'live',
    persona: 'HR Manager',
    permission: PERMISSIONS.BENEFITS_ENROLLMENTS_READ,
  },
  {
    id: 'benefits-vendors',
    label: 'Vendors',
    path: '/benefits/vendors',
    icon: HeartHandshake,
    description: 'Broker, carrier, provider and billing contacts for every plan.',
    sprint: 'P8_BENEFITS',
    status: 'live',
    persona: 'HR Manager',
    permission: PERMISSIONS.BENEFITS_PLANS_READ,
  },
  {
    id: 'benefits-costs',
    label: 'Costs',
    path: '/benefits/costs',
    icon: Coins,
    description: 'Monthly and annual cost by category and by plan, employer versus employee.',
    sprint: 'P8_BENEFITS',
    status: 'live',
    persona: 'HR Manager / CFO',
    permission: PERMISSIONS.BENEFITS_PLANS_READ,
  },
  {
    id: 'benefits-claims',
    label: 'Claims',
    path: '/benefits/claims',
    icon: FileCheck2,
    description: 'Benefits claims and their lifecycle from open to closed.',
    sprint: 'P8_BENEFITS',
    status: 'live',
    persona: 'HR Manager',
    permission: PERMISSIONS.BENEFITS_CLAIMS_READ,
  },
  {
    id: 'benefits-compliance',
    label: 'Compliance',
    path: '/benefits/compliance',
    icon: ShieldCheck,
    description: 'Written policies and procedures, and the renewal and effective-date calendar.',
    sprint: 'P8_BENEFITS',
    status: 'live',
    persona: 'HR Manager',
    permission: PERMISSIONS.BENEFITS_PLANS_READ,
  },
  {
    id: 'benefits-documents',
    label: 'Documents',
    path: '/benefits/documents',
    icon: FileText,
    description: 'Plan and general benefits documents — stored files and carrier portal links.',
    sprint: 'P8_BENEFITS',
    status: 'live',
    persona: 'HR Manager',
    permission: PERMISSIONS.BENEFITS_PLANS_READ,
  },
  {
    id: 'benefits-reports',
    label: 'Reports',
    path: '/benefits/reports',
    icon: BarChart3,
    description: 'Health check detail, recommendations and the activity log.',
    sprint: 'P8_BENEFITS',
    status: 'live',
    persona: 'HR Manager / Owner',
    permission: PERMISSIONS.BENEFITS_PLANS_READ,
  },
  {
    id: 'estimating-pipeline',
    label: 'Pipeline',
    path: '/estimating/pipeline',
    icon: Workflow,
    description:
      'One run that drives package, vendor cost, labour, and bid assembly. Opened from Company cost, not a separate intake.',
    sprint: 'P6_ESTIMATING_AUTOMATION',
    status: 'live',
    persona: 'Chief Estimator / Estimator',
    purpose:
      'Turn a drawing set into three bids without opening five screens, and see plainly which lines nobody has an answer for.',
    capabilities: [
      'Six stages in one run, with live progress as each engine finishes',
      'Every stage records what it produced and what it could not',
      'Blocked lines link to the screen that owns the fix',
      'Ends at a recommended bid; the signature stays with the estimator',
    ],
    permission: PERMISSIONS.ESTIMATING_PIPELINE_READ,
  },
  {
    id: 'estimating-command',
    label: 'Bid Command Center',
    path: '/estimating/command',
    icon: Calculator,
    description: 'Your next decisions across every pursuit, with source-linked readiness.',
    sprint: 'P9_ESTIMATING_WORKSPACE',
    status: 'live',
    persona: 'Owner / Estimator',
    purpose: 'Surface the decisions, blockers and deadlines across every active pursuit.',
    capabilities: ['Attention queue from source state', 'Readiness per pursuit', 'Source-linked briefing'],
    permission: PERMISSIONS.ESTIMATING_ESTIMATES_READ,
  },
  {
    id: 'estimating-projects',
    label: 'Projects',
    path: '/estimating/projects',
    icon: Calculator,
    description: 'Every pursuit as one connected workspace from package to submitted bid.',
    sprint: 'P9_ESTIMATING_WORKSPACE',
    status: 'live',
    persona: 'Owner / Estimator',
    purpose: 'Open a pursuit and work package, scope, materials, trades, resources, schedule, cost and bid in one place.',
    capabilities: ['Project bid workspace', 'Package and scope review', 'Trade quote comparison', 'Resource and schedule planning'],
    permission: PERMISSIONS.ESTIMATING_ESTIMATES_READ,
  },
  {
    id: 'bid-register',
    label: 'Bid Register',
    path: '/estimating/register',
    icon: History,
    description: 'All submitted bids, responses and follow-ups.',
    sprint: 'P9_ESTIMATING_WORKSPACE',
    status: 'live',
    persona: 'Owner / Estimator',
    purpose: 'Track submitted bids, their outcomes, contacts and reviewable follow-up drafts.',
    capabilities: ['Submitted bid register', 'Follow-up due queue', 'Unsent AI follow-up drafts'],
    permission: PERMISSIONS.ESTIMATING_ESTIMATES_READ,
  },
  {
    id: 'estimating-intelligence',
    label: 'Intelligence',
    path: '/estimating/intelligence',
    icon: BarChart3,
    description: 'Client and subcontractor intelligence, comparable projects and bid learning.',
    sprint: 'P9_ESTIMATING_WORKSPACE',
    status: 'live',
    persona: 'Owner / Chief Estimator',
    purpose: 'Turn recorded bid history and reviewed debriefs into guidance for the next bid.',
    capabilities: ['Client bid history', 'Subcontractor quote history', 'Win/loss debriefs'],
    permission: PERMISSIONS.ESTIMATING_ESTIMATES_READ,
  },
  {
    id: 'bid-intake',
    label: 'Intake',
    path: '/estimating/bid-intake',
    icon: ScanSearch,
    description:
      'The sponsor email, parsed into an invitation and a working record the team can qualify.',
    sprint: 'P4_BID_INTAKE',
    status: 'live',
    persona: 'Owner / Estimator',
    purpose:
      'Read the bid package out of the company mailbox into a record, then keep that record where the team decides whether to price it.',
    capabilities: [
      'Mailbox and Teams invitations with field-level provenance',
      'Accept into a working record or decline with a reason',
      'Working records scored against the go/no-go profile',
      'Qualify, decline, or no-bid stays a human decision',
    ],
    permission: PERMISSIONS.BID_INTAKE_INVITATIONS_READ,
    anyPermissions: [PERMISSIONS.BID_INTAKE_INVITATIONS_READ, PERMISSIONS.ESTIMATING_LEADS_READ],
  },
  {
    id: 'estimating-config',
    label: 'Config',
    path: '/estimating/config',
    icon: Settings,
    description:
      'Go/no-go criteria weights and estimating overhead, margin, contingency, and capacity.',
    sprint: 'M04_ESTIMATING_I',
    status: 'live',
    persona: 'Chief Estimator / Owner',
    purpose:
      'Edit the tenant criteria profile and estimating settings that score inbound work and price estimates.',
    capabilities: [
      'Explicit initial setup and versioned updates to go/no-go criteria',
      'Reviewed overhead, markup band, contingency and capacity settings',
      'Exact-request receipts, version checks and immutable change history',
    ],
    anyPermissions: [
      PERMISSIONS.ESTIMATING_SETTINGS_READ,
      PERMISSIONS.ESTIMATING_CRITERIA_PROFILES_READ,
    ],
  },
  {
    id: 'bid-assembly',
    label: 'Bid Assembly',
    path: '/bid-assembly',
    icon: Gavel,
    description:
      'Sell scenarios from one approved company cost revision, with assigned human approval and an evidence package.',
    sprint: 'M18_BID_ASSEMBLY',
    status: 'live',
    persona: 'Chief Estimator',
    purpose:
      'Turn a priced scope into three defensible numbers and the reasons they differ, so the person signing the bid is choosing a position rather than accepting a total.',
    capabilities: [
      'Margin taken ON SELL — price = cost ÷ (1 − margin), not cost × (1 + margin)',
      'A bond quoted on the contract price is solved, not charged against a base that excludes it',
      'Low / target / high share the same approved cost and explicit stored markup settings',
      'Assigned independent review pins scope, source fingerprint, tier and due date',
      'Private evidence packages retain exact pricing and approval; submission remains a separate receipt',
    ],
    permission: PERMISSIONS.BIDDING_ASSEMBLIES_READ,
  },
  {
    // NOT `bid-history`: M05 already owns that id for the PORTFOLIO-wide
    // win/loss analytics at /estimating/history. This is the per-counterparty
    // register — who was bid to, and whether their history supports a pattern
    // at all — which is a different question and deserves its own screen.
    id: 'bid-counterparties',
    label: 'Counterparties',
    path: '/bid-counterparties',
    icon: Handshake,
    description:
      'Every counterparty this company has bid to, with the outcome and — where anybody said — the winning number.',
    sprint: 'M18_BID_ASSEMBLY',
    status: 'live',
    persona: 'Chief Estimator / Owner',
    purpose:
      'Show where the bidding record is thick enough to narrow a window and where it is not, so a pattern is only read when there is one.',
    capabilities: [
      'A missing winning number is never imputed — it is counted and excluded',
      'Every recommendation states its sample: how many prior bids, how many with a known winner',
      'Below the stated minimum the platform refuses to sharpen and says why',
      'Selection bias named: you learn what won mostly on jobs you lost',
      'A portfolio-wide figure is offered only as a labelled fallback, never as this counterparty’s number',
    ],
    permission: PERMISSIONS.BIDDING_PATTERNS_READ,
    allPermissions: [PERMISSIONS.ESTIMATING_ESTIMATES_READ],
  },
  {
    id: 'bank-reconciliation',
    label: 'Bank Reconciliation',
    path: '/banking/reconciliation',
    icon: ScanSearch,
    description:
      'Proposed matches between bank transactions and payables, receivables and job cost.',
    sprint: 'M14_PLAID_BANKING',
    status: 'live',
    persona: 'Owner / Finance',
    purpose:
      'Confirm or reject what the matcher believes each transaction is, and see the spend nothing in the platform accounts for.',
    capabilities: [
      'Rules-first matching on amount, date proximity and vendor name',
      'Confidence and stated evidence on every proposal',
      'A human confirms; nothing is auto-posted as fact',
    ],
    permission: PERMISSIONS.BANKING_RECONCILIATION_READ,
  },
  {
    id: 'settings',
    label: 'Organization',
    path: '/settings',
    icon: Settings,
    description: 'Workspace, users, security, audit, and platform administration.',
    sprint: 'S03_AUDIT_NOTIFICATIONS_SETTINGS',
    status: 'live',
    persona: 'Admin',
    purpose:
      'Settings workspace: profile, organization, users, roles, tenants, audit, and platform tools.',
    capabilities: [
      'Company and org settings',
      'Users, roles and permissions',
      'Tenant and platform administration',
      'Audit trail and activity',
    ],
    permission: PERMISSIONS.SETTINGS_MANAGE,
  },
  {
    id: 'settings-portal',
    label: 'Partner portal',
    path: '/settings/portal',
    icon: Users,
    description:
      'External invitations, company access, onboarding requirements, and portal finance approvals.',
    sprint: 'M11_PARTNER_PORTAL',
    status: 'live',
    persona: 'Admin / Finance',
    purpose: 'Manage the people and records shared with each client, vendor, and subcontractor.',
    capabilities: [
      'Invitations and revocation',
      'Project grants',
      'Document compliance review',
      'Invoice review and payout requests',
    ],
    permission: PERMISSIONS.PORTAL_CONTACTS_READ,
    anyPermissions: [
      PERMISSIONS.PORTAL_CONTACTS_READ,
      PERMISSIONS.PORTAL_MESSAGES_READ,
      PERMISSIONS.PORTAL_DOCUMENT_SHARES_READ,
      'finance:ap_invoices:read',
      PERMISSIONS.INTEGRATIONS_CONNECTIONS_READ,
    ],
  },
  {
    id: 'settings-preferences',
    label: 'Preferences',
    path: '/settings/preferences',
    icon: UserCog,
    description: 'Your profile, timezone, display and notification preferences.',
    sprint: 'S03_AUDIT_NOTIFICATIONS_SETTINGS',
    status: 'live',
    persona: 'All staff',
    purpose: 'Personal preferences for the signed-in user.',
    capabilities: [
      'Profile name and title',
      'Timezone and date format',
      'Landing page and dense mode',
      'Critical-only alerts and saved digest status',
    ],
  },
  {
    id: 'settings-notifications',
    label: 'Notifications',
    path: '/settings/notifications',
    icon: Bell,
    description: 'Your in-app notification inbox.',
    sprint: 'S03_AUDIT_NOTIFICATIONS_SETTINGS',
    status: 'live',
    persona: 'Admin',
    purpose: 'Read and acknowledge your notifications.',
    capabilities: ['In-app notification inbox', 'Mark read / mark all read'],
    permission: PERMISSIONS.NOTIFICATIONS_READ,
  },
  {
    id: 'settings-feature-flags',
    label: 'Feature flags',
    path: '/settings/feature-flags',
    icon: Flag,
    description: 'Tenant-scoped feature flag enablement and rollout controls.',
    sprint: 'S03_AUDIT_NOTIFICATIONS_SETTINGS',
    status: 'live',
    persona: 'Admin',
    purpose: 'Enable, disable, and roll out tenant feature flags from the settings API.',
    capabilities: ['Flag catalog', 'Enable and disable', 'Rollout percent'],
    permission: PERMISSIONS.SETTINGS_MANAGE,
  },
  {
    id: 'settings-ai',
    label: 'AI assistant',
    path: '/settings/ai',
    icon: Bot,
    description: 'Live AI provider health and task-class routing for this deployment.',
    sprint: 'S03_AUDIT_NOTIFICATIONS_SETTINGS',
    status: 'live',
    persona: 'Admin',
    purpose:
      'Configure AI provider keys and models, assistant preferences, advisor agents, knowledge, and guardrails.',
    capabilities: ['Provider health', 'Task-class routes', 'Refresh from the live status API'],
    permission: PERMISSIONS.SYSTEM_AI_PROVIDERS_READ,
  },
  {
    id: 'settings-access',
    label: 'Access control',
    path: '/settings/access',
    icon: Lock,
    description: 'Roles, permissions, membership, and tenant access policy.',
    sprint: 'S03_AUDIT_NOTIFICATIONS_SETTINGS',
    status: 'live',
    persona: 'Admin',
    purpose:
      'First-class access surface: membership, role grants, and tenant MFA/sharing policy. Session inventory stays on Activity. Idle JWT refresh is not edited here.',
    capabilities: [
      'Membership directory',
      'Roles and permission counts',
      'Require MFA',
      'External sharing',
      'Default notification channel',
    ],
    permission: PERMISSIONS.SETTINGS_MANAGE,
  },
  {
    id: 'settings-retention',
    label: 'Data retention',
    path: '/settings/retention',
    icon: Archive,
    description: 'Audit-log retention years stored on the organization settings row.',
    sprint: 'S03_AUDIT_NOTIFICATIONS_SETTINGS',
    status: 'live',
    persona: 'Admin',
    purpose: 'Set how long tenant audit records are kept. No fabricated retention schedule.',
    capabilities: ['Audit retention years', 'Save through the settings API'],
    permission: PERMISSIONS.SETTINGS_MANAGE,
  },
  {
    id: 'settings-plan',
    label: 'Workspace plan',
    path: '/settings/plan',
    icon: CreditCard,
    description: 'Live tenant plan name and workspace status. No fabricated invoices.',
    sprint: 'S03_AUDIT_NOTIFICATIONS_SETTINGS',
    status: 'live',
    persona: 'Admin',
    purpose:
      'Show the Tenant.plan and status fields as stored. Billing amounts are omitted until a real billing provider exists.',
    capabilities: [
      'Plan name from Tenant.plan',
      'Workspace status',
      'Empty state when no plan is assigned',
    ],
    permission: PERMISSIONS.SETTINGS_MANAGE,
  },
];

/**
 * Authentication plus the platform-admin tools. They remain first-class
 * catalog entries (routes, RBAC, live pages) but are not in the left rail:
 * `/auth` is reached by signing out; the rest live in the Settings workspace.
 */
const SYSTEM_DESTINATIONS: NavDestination[] = [
  {
    id: 'auth',
    label: 'Sign in',
    path: '/auth',
    icon: ShieldCheck,
    description: 'Authentication, tenants, roles and session handling.',
    sprint: 'S02_AUTH_RBAC_TENANCY',
    status: 'live',
    persona: 'All Users',
    purpose: 'Authentication, tenants, users, roles, permissions, guards and session handling.',
    capabilities: [
      'Email / SSO authentication',
      'Tenant and role binding',
      'JWT access and refresh',
      'Session and guard enforcement',
    ],
  },
  {
    id: 'build-status',
    label: 'Build Status',
    path: '/settings/build-status',
    icon: ListChecks,
    description:
      'Workspace development checklist, review evidence, and packaged Engineering Bible roadmap.',
    sprint: 'S00_REPO_FOUNDATION',
    status: 'live',
    persona: 'Platform administrator',
    purpose:
      'Track outstanding development work, owner-provided checklist items, and reviewed completion evidence.',
    capabilities: [
      'Preview and import owner-provided checklist rows',
      'Record progress and review evidence with an audit trail',
      'Packaged roadmap acceptance records',
      'Workspace-specific versioned tracking',
    ],
    permission: PERMISSIONS.PLATFORM_ADMIN,
  },
  {
    id: 'seed-data',
    label: 'Seed Data',
    path: '/settings/seed-data',
    icon: Database,
    description: 'Enterprise seed coverage and audited domain expansion runs.',
    sprint: 'S32_SEED_DATA',
    status: 'live',
    persona: 'Platform Admin',
    purpose:
      'Inspect live domain coverage against Bible enterprise targets and expand seeded catalogs in audited batches.',
    capabilities: [
      'Live domain coverage vs Bible targets',
      'Audited batch expansion',
      'SeedRun history',
      'Platform-admin gated controls',
    ],
    permission: PERMISSIONS.PLATFORM_ADMIN,
  },
  {
    id: 'testing',
    label: 'Testing Automation',
    path: '/settings/testing',
    icon: ClipboardCheck,
    description: 'Critical journey catalog and audited automation plan runs.',
    sprint: 'S33_TESTING_AUTOMATION',
    status: 'live',
    persona: 'Platform Admin / QA',
    purpose:
      'Inventory critical unit/integration/component/E2E journeys and run audited on-disk critical plans.',
    capabilities: [
      'Critical journey catalog',
      'Suite kind coverage',
      'Audited critical plan runs',
      'Spec presence checks',
    ],
    permission: PERMISSIONS.PLATFORM_ADMIN,
  },
  {
    id: 'security',
    label: 'Security Hardening',
    path: '/settings/security',
    icon: ShieldCheck,
    description: 'Tenant isolation, RBAC, audit, and secrets hygiene scans.',
    sprint: 'S34_SECURITY_HARDENING',
    status: 'live',
    persona: 'Platform Admin / Security',
    purpose:
      'Run and review security hardening controls for tenant isolation, RBAC, audit coverage, and secrets hygiene.',
    capabilities: [
      'Security control catalog',
      'Audited scan runs',
      'Finding severity triage',
      'Secrets hygiene checks',
    ],
    permission: PERMISSIONS.PLATFORM_ADMIN,
  },
  {
    id: 'infra',
    label: 'Infrastructure',
    path: '/settings/infra',
    icon: CloudCog,
    description: 'Docker, CI/CD, environment profiles, health, and deploy runbooks.',
    sprint: 'S35_INFRA_DEPLOYMENT',
    status: 'live',
    persona: 'Platform Admin / DevOps',
    purpose:
      'Inventory deployment artifacts and run audited readiness checks against Docker, CI, env, health, and runbooks.',
    capabilities: [
      'Deploy artifact catalog',
      'Audited readiness runs',
      'Health and Redis posture',
      'Environment profile inventory',
    ],
    permission: PERMISSIONS.PLATFORM_ADMIN,
  },
  {
    id: 'observability',
    label: 'Observability',
    path: '/settings/observability',
    icon: Activity,
    description: 'Cache, metrics, traces, logs, and performance passes.',
    sprint: 'S36_PERFORMANCE_OBSERVABILITY',
    status: 'live',
    persona: 'Platform Admin / SRE',
    purpose: 'Inspect live HTTP metrics/traces/cache hit rates and run audited performance passes.',
    capabilities: [
      'Live metrics dashboard',
      'Trace ring buffer',
      'TTL cache telemetry',
      'Audited performance passes',
    ],
    permission: PERMISSIONS.PLATFORM_ADMIN,
  },
  {
    id: 'launch',
    label: 'Launch Polish',
    path: '/settings/launch',
    icon: Rocket,
    description: 'Accessibility, responsive, smoke, and release checklist readiness.',
    sprint: 'S37_LAUNCH_POLISH',
    status: 'live',
    persona: 'Platform Admin / Release Owner',
    purpose:
      'Run audited launch smoke against accessibility, responsive, smoke, and release checklist controls.',
    capabilities: [
      'Launch checklist catalog',
      'Audited smoke runs',
      'Release checklist inventory',
      'Live destination polish gate',
    ],
    permission: PERMISSIONS.PLATFORM_ADMIN,
  },
  {
    id: 'client-launch',
    label: 'Client Launch Center',
    path: '/client-launch',
    icon: Handshake,
    description:
      'Private implementation workspace: discovery, vendor spend, documents, integrations, and launch readiness.',
    sprint: 'P8_CLIENT_LAUNCH_CENTER',
    status: 'live',
    persona: 'Client ops / Helios implementation',
    purpose:
      'Permanent Helios ↔ client shared workspace from discovery through optimization — confirm exceptions, do not rekey everything.',
    capabilities: [
      'Guided onboarding stages',
      'Vendor & spend inventory (14 categories)',
      'Smart attention inbox',
      'Document vault & spend workbook import',
      'Integration readiness',
      'Decision / question / task registers',
      'Weighted launch readiness',
      'Master tracker with owners and completion requirements',
      'Create, edit, and delete launch tasks',
      'Helios control room',
    ],
    permission: PERMISSIONS.CLIENT_LAUNCH_READ,
  },
  {
    id: 'client-launch-readiness',
    label: 'Launch readiness',
    path: '/client-launch/readiness',
    icon: ListChecks,
    description:
      'Evaluate platform gaps and manage the master launch tracker with accountable owners.',
    sprint: 'P8_CLIENT_LAUNCH_CENTER',
    status: 'live',
    persona: 'Helios implementation manager',
    purpose:
      'Compare current platform readiness with the master tracker and manage outstanding work.',
    capabilities: [
      'Current platform evaluation with evidence boundaries',
      'Imported master tracker and its source snapshot',
      'Outstanding work, owners, and completion requirements',
      'Editable launch task register',
    ],
    permission: PERMISSIONS.CLIENT_LAUNCH_MANAGE,
  },
  {
    id: 'settings-users',
    label: 'Users',
    path: '/settings/users',
    icon: Users,
    description: 'Create users, assign roles, and deactivate accounts.',
    sprint: 'S02_AUTH_RBAC_TENANCY',
    status: 'live',
    persona: 'Admin',
    purpose: 'Administer users inside the tenant, or across tenants as a platform admin.',
    capabilities: ['Create user', 'Assign roles', 'Deactivate account'],
    permission: PERMISSIONS.USERS_MANAGE,
  },
  {
    id: 'settings-roles',
    label: 'Roles & permissions',
    path: '/settings/roles',
    icon: KeyRound,
    description: 'Role grants for this tenant and system roles.',
    sprint: 'S02_AUTH_RBAC_TENANCY',
    status: 'live',
    persona: 'Admin',
    purpose: 'Inspect and edit role permission grants.',
    capabilities: ['Create tenant role', 'Edit grants', 'System role review'],
    permission: PERMISSIONS.ROLES_MANAGE,
  },
  {
    id: 'settings-tenants',
    label: 'Subscribers & tenants',
    path: '/settings/tenants',
    icon: Building2,
    description: 'Invite company owners and manage customer workspaces.',
    sprint: 'S02_AUTH_RBAC_TENANCY',
    status: 'live',
    persona: 'Platform Admin',
    purpose:
      'Invite verified owners, track onboarding, and update workspace name and status within your administrative scope.',
    capabilities: [
      'Email owner invitations',
      'Resend and revoke invitations',
      'List tenants',
      'Rename',
      'Change status',
    ],
    permission: PERMISSIONS.PLATFORM_ADMIN,
  },
  {
    id: 'settings-audit',
    label: 'Audit log',
    path: '/settings/audit',
    icon: ClipboardList,
    description: 'Who changed what, and when.',
    sprint: 'S03_AUDIT_NOTIFICATIONS_SETTINGS',
    status: 'live',
    persona: 'Admin / Security',
    purpose: 'Read tenant or platform audit events.',
    capabilities: ['Filter by action', 'Filter by tenant', 'Paginated trail'],
    permission: PERMISSIONS.AUDIT_LOGS_READ,
  },
  {
    id: 'settings-purge',
    label: 'Danger zone',
    path: '/settings/danger-zone',
    icon: ShieldAlert,
    description: 'Super-admin data purge: one tenant or a full platform reset.',
    sprint: 'S02_AUTH_RBAC_TENANCY',
    status: 'live',
    persona: 'Platform Super Admin',
    purpose:
      'Wipe business data back to a fresh state after a dry run, typed confirmation and password check.',
    capabilities: [
      'Dry-run row counts',
      'Tenant purge',
      'Full platform reset',
      'Immutable platform audit',
    ],
    permission: PERMISSIONS.PLATFORM_ADMIN,
  },
  {
    id: 'settings-activity',
    label: 'Activity',
    path: '/settings/activity',
    icon: Activity,
    description: 'Sign-in sessions across the tenant or platform.',
    sprint: 'S02_AUTH_RBAC_TENANCY',
    status: 'live',
    persona: 'Admin / Security',
    purpose: 'Inspect live and revoked authentication sessions.',
    capabilities: ['Session inventory', 'Tenant filter', 'Active vs revoked'],
    permission: PERMISSIONS.USERS_MANAGE,
  },
  {
    id: 'project-map',
    label: 'Project Map',
    path: '/projects/map',
    icon: MapPinned,
    description: 'Realtime project locations, risk, alerts and portfolio exposure.',
    sprint: 'S05_REALTIME_MAPS',
    status: 'live',
    persona: 'Executive / Operations',
    purpose: 'Realtime map of project health, risk, location, alerts and live event updates.',
    capabilities: [
      'Tenant-scoped project markers',
      'Risk and alert overlays',
      'Portfolio exposure summary',
      'Authenticated WebSocket event replay',
    ],
    permission: PERMISSIONS.PROJECTS_READ,
  },
];

/** Every destination the product owns, in no particular order. */
const CATALOG: NavDestination[] = [...PROJECT_DESTINATIONS, ...SYSTEM_DESTINATIONS];

const CATALOG_BY_ID = new Map(CATALOG.map((destination) => [destination.id, destination]));

/**
 * Platform-admin destinations. These used to be the sidebar `Platform` domain.
 * They remain first-class catalog entries (routes, RBAC, command-center pages)
 * but they are reached from the Settings workspace left rail, not the main rail.
 */
export const SETTINGS_MENU_DESTINATION_IDS = [
  'settings',
  'settings-users',
  'settings-tenants',
  'integrations',
] as const;

/**
 * Destinations that exist only for non-production fixture tooling. Production
 * builds hide them (the API also refuses them) so a new client never sees a
 * seed generator.
 */
const NON_PRODUCTION_DESTINATION_IDS = new Set<string>(['seed-data']);
export function visibleInThisBuild<T extends string>(
  ids: readonly T[],
  production: boolean = import.meta.env.PROD,
): T[] {
  return production ? ids.filter((id) => !NON_PRODUCTION_DESTINATION_IDS.has(id)) : [...ids];
}

export const SETTINGS_MENU_DESTINATIONS: NavDestination[] = visibleInThisBuild(
  SETTINGS_MENU_DESTINATION_IDS,
).map((id) => {
  const destination = CATALOG_BY_ID.get(id);
  if (!destination) {
    throw new Error(`Settings menu references unknown destination id: ${id}`);
  }
  return destination;
});

/**
 * Workspace admin sections rendered in the Settings shell sidebar. Avatar
 * flyout stays on SETTINGS_MENU_DESTINATION_IDS (high-level entry points).
 */
export const SETTINGS_WORKSPACE_DESTINATION_IDS = [
  'settings',
  'settings-preferences',
  'settings-access',
  'settings-notifications',
  'settings-feature-flags',
  'settings-ai',
  'integrations',
] as const;

/**
 * Super-admin sections. Each id is permission-gated in the catalog, so a
 * tenant user who holds none of them never sees the group at all.
 */
export const SETTINGS_ADMIN_DESTINATION_IDS = [
  'settings-portal',
  'settings-users',
  'settings-roles',
  'settings-tenants',
  'settings-audit',
  'settings-activity',
  'settings-purge',
] as const;

export const SETTINGS_PLATFORM_DESTINATION_IDS = [
  'build-status',
  'seed-data',
  'testing',
  'security',
  'infra',
  'observability',
  'launch',
] as const;

export interface SettingsNavGroup {
  id: 'workspace' | 'administration' | 'platform';
  label: string;
  items: NavDestination[];
}

function destinationsForIds(ids: readonly string[]): NavDestination[] {
  return ids.map((id) => {
    const destination = CATALOG_BY_ID.get(id);
    if (!destination) {
      throw new Error(`Settings nav references unknown destination id: ${id}`);
    }
    return destination;
  });
}

export function settingsNavGroupsForPermissions(
  permissions: readonly string[],
): SettingsNavGroup[] {
  const groups: SettingsNavGroup[] = [
    {
      id: 'workspace',
      label: 'Workspace',
      items: filterSettingsMenuForPermissions(
        destinationsForIds(SETTINGS_WORKSPACE_DESTINATION_IDS),
        permissions,
      ),
    },
    {
      id: 'administration',
      label: 'Administration',
      items: filterSettingsMenuForPermissions(
        destinationsForIds(SETTINGS_ADMIN_DESTINATION_IDS),
        permissions,
      ),
    },
    {
      id: 'platform',
      label: 'Platform',
      items: filterSettingsMenuForPermissions(
        destinationsForIds(visibleInThisBuild(SETTINGS_PLATFORM_DESTINATION_IDS)),
        permissions,
      ),
    },
  ];
  return groups.filter((group) => group.items.length > 0);
}

/** Sidebar label: the catalog entry is still "Settings" for the avatar flyout. */
export function settingsSectionLabel(destination: NavDestination): string {
  return destination.id === 'settings' ? 'Organization' : destination.label;
}

const SETTINGS_MENU_ID_SET = new Set<string>(SETTINGS_MENU_DESTINATION_IDS);

export function isSettingsMenuDestination(id: string): boolean {
  return SETTINGS_MENU_ID_SET.has(id);
}

/**
 * Former top-level Platform URLs. Bookmarks and in-app links still resolve;
 * the router redirects them into the Settings shell.
 */
export const LEGACY_SETTINGS_REDIRECTS: Readonly<Record<string, string>> = {
  '/integrations': '/settings/integrations',
  '/build-status': '/settings/build-status',
  '/seed-data': '/settings/seed-data',
  '/testing': '/settings/testing',
  '/security': '/settings/security',
  '/infra': '/settings/infra',
  '/observability': '/settings/observability',
  '/launch': '/settings/launch',
};

/**
 * Former standalone URLs that now live as a hub tab. Bookmarks still resolve.
 */
export const LEGACY_HUB_REDIRECTS: Readonly<Record<string, string>> = {
  '/hr/benefits': '/benefits',
  '/hr/roster': '/hr/employees',
  '/estimating/leads': '/estimating/bid-intake',
  // Client Accounts and Vendors were absorbed into Relationships.
  '/clients': '/crm/clients',
  '/vendors': '/crm/vendors',
  '/vendors/ingest': '/crm/vendors/ingest',
};

/** Retired standalone pages resolve to their current workspace. */
export const LEGACY_PAGE_REDIRECTS: Readonly<Record<string, string>> = {
  '/dashboards/owner': '/',
};

export const LEGACY_PATH_REDIRECTS: Readonly<Record<string, string>> = {
  ...LEGACY_SETTINGS_REDIRECTS,
  ...LEGACY_HUB_REDIRECTS,
  ...LEGACY_PAGE_REDIRECTS,
};

/**
 * The nine corporate-department domains in the left rail.
 *
 * P10 regrouped around the departments a construction company actually has.
 * P11 (this pass) finishes the department labels: the catch-all `overview`
 * rail becomes **Executive Management**, and **Technology** takes the AI
 * Agents hub. P12 collapses sibling screens into subject hubs (`SUBJECT_HUBS`)
 * so the rail shows one row per subject; child destinations stay catalogued
 * and become AUX. See docs/implementation_decisions.md.
 *
 * Placement rule: a destination belongs to the domain of the QUESTION it
 * answers, not the sprint that built it or the URL it happens to sit on.
 * Admin / prove-the-build tools live in the account Settings menu, not here.
 */
const DOMAIN_BLUEPRINT: NavDomainBlueprint[] = [
  {
    id: 'executive-management',
    label: 'Executive Management',
    icon: LayoutDashboard,
    summary: 'Where the business stands today, and the decisions waiting on you.',
    keywords: ['home', 'dashboard', 'executive', 'portfolio', 'kpi', 'management', 'my day'],
    destinations: [
      'executive-summary',
      'm365-workspace',
      'google-workspace',
      'ops-dashboard',
      'growth-ma',
    ],
  },
  {
    id: 'preconstruction',
    label: 'Preconstruction',
    icon: Calculator,
    summary: 'Everything between an invitation to bid and a number you can sign.',
    keywords: ['estimating', 'bidding', 'pursuit', 'business development', 'precon'],
    destinations: ['estimates', 'market-intelligence', 'competitors', 'portfolio-showcases'],
  },
  {
    id: 'operations',
    label: 'Operations',
    icon: HardHat,
    summary: 'Delivering the work that was won — office and field together.',
    keywords: [
      'jobs',
      'delivery',
      'project management',
      'controls',
      'site',
      'crew',
      'superintendent',
      'safety',
      'quality',
    ],
    destinations: [
      'project-portfolio',
      'project-overview',
      'project-map',
      'documents',
      'digital-twin',
      'field-today',
      'site-telemetry',
      'safety',
      'quality',
      'approvals',
      'resources',
    ],
  },
  {
    id: 'finance',
    label: 'Finance',
    icon: BadgeDollarSign,
    summary: 'Money earned, money billed, money held and money in the bank.',
    keywords: [
      'accounting',
      'cash',
      'billing',
      'banking',
      'payroll',
      'quickbooks',
      'insurance',
      'policies',
      'debt',
      'loans',
      'customers',
      'vendors',
    ],
    destinations: ['finance', 'payroll-exports', 'bank-accounts', 'insurance', 'debt'],
  },
  {
    id: 'hr',
    label: 'HR',
    icon: Users,
    summary: 'The people who work here, and what they are enrolled in.',
    keywords: [
      'people',
      'roster',
      'employees',
      'onboarding',
      'benefits',
      'enrollment',
      'claims',
      'compliance',
    ],
    destinations: ['hr', 'benefits', 'time-automation'],
  },
  {
    id: 'legal',
    label: 'Legal',
    icon: Gavel,
    summary:
      'Matters, counsel, the corporate structure, and the contracts everything else runs on.',
    keywords: [
      'matters',
      'counsel',
      'litigation',
      'entities',
      'ownership',
      'contracts',
      'clauses',
      'compliance',
      'corporate',
    ],
    destinations: ['legal-overview'],
  },
  {
    id: 'clients-vendors',
    label: 'Sales & Vendors',
    icon: Handshake,
    summary: 'The people outside the company: who owes what, and who may work.',
    keywords: [
      'crm',
      'customers',
      'prospects',
      'pipeline',
      'subcontractors',
      'suppliers',
      'compliance',
      'email',
      'marketing',
      'social',
      'instagram',
      'facebook',
    ],
    destinations: [
      'sales-pipeline',
      'unified-crm',
      'inbox-triage',
      'compliance-board',
      'procurement',
      'client-launch',
      'marketing',
    ],
  },
  {
    id: 'technology',
    label: 'Technology',
    icon: Cpu,
    summary: 'The IT estate, the agent fleet, and the platform intelligence that runs it.',
    keywords: [
      'ai',
      'agents',
      'automation',
      'platform',
      'technology',
      'advisor',
      'hardware',
      'software',
      'licences',
      'cameras',
      'intune',
      'devices',
    ],
    destinations: [
      'it-estate',
      'ai-agents',
      'agent-detail',
      'finance-agent-detail',
      'scheduling-agent-detail',
      'procurement-agent-detail',
      'safety-agent-detail',
      'compliance-agent-detail',
      'estimating-agent-detail',
      'qa-qc-agent-detail',
      'hr-workforce-agent-detail',
      'equipment-agent-detail',
      'market-intelligence-agent-detail',
      'growth-ma-agent-detail',
    ],
  },
  {
    id: 'insights',
    label: 'Insights',
    icon: BrainCircuit,
    summary: 'Cross-cutting analysis: what-if scenarios and saved reporting.',
    keywords: ['analytics', 'reporting', 'simulation', 'scenarios'],
    destinations: ['simulations', 'reports'],
  },
];

function resolveDomain(blueprint: NavDomainBlueprint): NavDomain {
  return {
    id: blueprint.id,
    label: blueprint.label,
    icon: blueprint.icon,
    summary: blueprint.summary,
    keywords: blueprint.keywords,
    items: blueprint.destinations.map((id) => {
      const destination = CATALOG_BY_ID.get(id);
      if (!destination) {
        // A typo in the blueprint would otherwise silently delete a screen from
        // the product. Fail loudly instead — registry.test.ts asserts this.
        throw new Error(`Navigation blueprint references unknown destination id: ${id}`);
      }
      return destination;
    }),
  };
}

/** The nine-domain navigation tree rendered by the sidebar and the palette. */
export const NAV_GROUPS: NavDomain[] = DOMAIN_BLUEPRINT.map(resolveDomain);

/** Destinations placed in the left-rail navigation tree (flattened). */
export const NAV_DESTINATIONS: NavDestination[] = NAV_GROUPS.flatMap((group) => group.items);

const PLACED_IDS = new Set(NAV_DESTINATIONS.map((destination) => destination.id));

/**
 * Reachable routes deliberately absent from the left rail: `/auth` (reached by
 * signing out), Settings workspace destinations, and the
 * screens that are now tabs of a subject hub rather than sidebar rows of their
 * own. Catalog entries stay so deep links and command-palette jumps work.
 */
export const AUX_DESTINATIONS: NavDestination[] = CATALOG.filter(
  (destination) => !PLACED_IDS.has(destination.id),
);

export const ALL_DESTINATIONS: NavDestination[] = [...NAV_DESTINATIONS, ...AUX_DESTINATIONS];

/**
 * Proof that the blueprint and the catalog agree. Asserted by registry.test.ts
 * so a future re-grouping cannot quietly lose or duplicate a screen.
 */
export const NAV_TREE_AUDIT = {
  catalogSize: CATALOG.length,
  placed: NAV_DESTINATIONS.length,
  unplaced: AUX_DESTINATIONS.map((destination) => destination.id),
  duplicated: NAV_DESTINATIONS.filter(
    (destination, index) =>
      NAV_DESTINATIONS.findIndex((other) => other.id === destination.id) !== index,
  ).map((destination) => destination.id),
} as const;

/** Destinations rendered by a data-driven ModuleScaffold (not yet built). */
export const SCAFFOLD_DESTINATIONS: NavDestination[] = ALL_DESTINATIONS.filter(
  (destination) => destination.status === 'scaffold',
);

export function findDestinationByPath(path: string): NavDestination | undefined {
  const canonical = LEGACY_PATH_REDIRECTS[path] ?? path;
  return ALL_DESTINATIONS.find((destination) => destination.path === canonical);
}

export function findHubForPath(path: string): SubjectHub | undefined {
  let best: { hub: SubjectHub; length: number } | undefined;
  for (const hub of SUBJECT_HUBS) {
    for (const tab of hub.tabs) {
      const destPath = tab.destinationId ? CATALOG_BY_ID.get(tab.destinationId)?.path : tab.to;
      const prefixes = [destPath, ...(tab.activePaths ?? [])].filter((prefix): prefix is string =>
        Boolean(prefix),
      );
      for (const prefix of prefixes) {
        const matches = path === prefix || (prefix !== '/' && path.startsWith(`${prefix}/`));
        if (matches && prefix.length > (best?.length ?? 0)) {
          best = { hub, length: prefix.length };
        }
      }
    }
  }
  return best?.hub;
}

export function isRailDestinationActive(destinationId: string, pathname: string): boolean {
  const hub = findHubByRailDestinationId(destinationId);
  if (!hub) return false;
  return findHubForPath(pathname)?.id === hub.id;
}

/**
 * Attach hub-tab destinations to the rail tree so search and the command
 * palette still find Takeoff when the rail only shows Estimations & Bids.
 */
export function expandNavGroupsWithHubTabs(
  groups: readonly NavDomain[],
  permissions?: readonly string[],
): NavDomain[] {
  return groups.map((group) => {
    const extras: NavDestination[] = [];
    const seen = new Set(group.items.map((item) => item.id));
    for (const item of group.items) {
      const hub = findHubByRailDestinationId(item.id);
      if (!hub) continue;
      for (const tab of hub.tabs) {
        if (!tab.destinationId || seen.has(tab.destinationId)) continue;
        const destination = CATALOG_BY_ID.get(tab.destinationId);
        if (!destination) continue;
        if (permissions && !userCanAccessDestination(destination, permissions)) continue;
        seen.add(tab.destinationId);
        extras.push(destination);
      }
    }
    return extras.length > 0 ? { ...group, items: [...group.items, ...extras] } : group;
  });
}

export function findDestinationById(id: string): NavDestination | undefined {
  return CATALOG_BY_ID.get(id);
}

export function userCanAccessDestination(
  destination: NavDestination,
  permissions: readonly string[],
): boolean {
  if (destination.allPermissions?.some((permission) => !hasPermission(permissions, permission))) {
    return false;
  }
  if (destination.anyPermissions?.length) {
    return hasAnyPermission(permissions, destination.anyPermissions);
  }
  return !destination.permission || hasPermission(permissions, destination.permission);
}

/**
 * Sidebar / palette visibility. Hub rail rows use `railAnyPermissions` so a
 * sibling-tab grant can reveal the hub without opening the parent page.
 */
export function userCanSeeRailDestination(
  destination: NavDestination,
  permissions: readonly string[],
): boolean {
  if (destination.railAnyPermissions?.length) {
    if (hasAnyPermission(permissions, destination.railAnyPermissions)) return true;
    if (destination.id === 'finance') {
      const reports = CATALOG_BY_ID.get('quickbooks-reports');
      return Boolean(reports && userCanAccessDestination(reports, permissions));
    }
    return false;
  }
  return userCanAccessDestination(destination, permissions);
}

/**
 * Rail / palette href. When the caller can see the hub but not the parent
 * page, land on the first tab they may actually open.
 */
export function railPathFor(destination: NavDestination, permissions: readonly string[]): string {
  const hub = findHubByRailDestinationId(destination.id);
  if (hub?.phases?.length) {
    const processStart = firstAccessibleHubTabPath(hub, permissions);
    if (processStart) return processStart;
  }
  if (userCanAccessDestination(destination, permissions)) {
    return destination.path;
  }
  if (!hub) return destination.path;
  return firstAccessibleHubTabPath(hub, permissions) ?? destination.path;
}

function firstAccessibleHubTabPath(
  hub: SubjectHub,
  permissions: readonly string[],
): string | undefined {
  for (const tab of hub.tabs) {
    if (tab.destinationId) {
      const child = CATALOG_BY_ID.get(tab.destinationId);
      if (child && userCanAccessDestination(child, permissions)) return tab.to ?? child.path;
      const fallback = tab.fallbackDestinationId
        ? CATALOG_BY_ID.get(tab.fallbackDestinationId)
        : undefined;
      if (fallback && userCanAccessDestination(fallback, permissions)) return fallback.path;
      continue;
    }
    if (tab.to && tab.permission && hasPermission(permissions, tab.permission)) {
      return tab.to;
    }
  }
  return undefined;
}

export function filterSettingsMenuForPermissions(
  destinations: readonly NavDestination[],
  permissions: readonly string[],
): NavDestination[] {
  return destinations.filter((destination) => userCanAccessDestination(destination, permissions));
}

/**
 * Agent workspaces and hub-tab children stay routeable; keep them out of the
 * primary sidebar density. The rail shows the hub row only.
 */
function isPrimarySidebarDestination(item: NavDestination): boolean {
  if (item.id === 'agent-detail' || item.id.endsWith('-agent-detail')) return false;
  if (isCollapsedNavDestination(item.id)) return false;
  return !isHubTabDestinationId(item.id);
}

/**
 * RBAC gate for the navigation tree. A domain whose children are all denied is
 * dropped entirely — an expandable section that opens onto nothing is worse
 * than no section at all, and it leaks the shape of what the user may not see.
 */
export function filterNavGroupsForPermissions(
  groups: readonly NavDomain[],
  permissions: readonly string[],
): NavDomain[] {
  return groups
    .map((group) => ({
      ...group,
      items: group.items.filter(
        (item) => isPrimarySidebarDestination(item) && userCanSeeRailDestination(item, permissions),
      ),
    }))
    .filter((group) => group.items.length > 0);
}

/**
 * Operator-chosen rail visibility. Hidden pages stay in the catalog, command
 * palette, and routes — only the sidebar and mobile drawer drop them.
 */
export function filterNavGroupsForHiddenPages(
  groups: readonly NavDomain[],
  hiddenIds: readonly string[],
): NavDomain[] {
  if (hiddenIds.length === 0) {
    return [...groups];
  }
  const hidden = new Set(hiddenIds);
  return groups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => !hidden.has(item.id)),
    }))
    .filter((group) => group.items.length > 0);
}

/**
 * The domain that owns a route, used to auto-expand the section you are in.
 * Falls back to a prefix match so a child route (e.g. /field/daily-reports/42)
 * still lights up its parent domain.
 */
export function findDomainForPath(
  groups: readonly NavDomain[],
  path: string,
): NavDomain | undefined {
  const hub = findHubForPath(path);
  if (hub) {
    const byRail = groups.find((group) =>
      group.items.some((item) => item.id === hub.railDestinationId),
    );
    if (byRail) return byRail;
  }

  const exact = groups.find((group) => group.items.some((item) => item.path === path));
  if (exact) return exact;

  let best: { group: NavDomain; length: number } | undefined;
  for (const group of groups) {
    for (const item of group.items) {
      if (
        item.path !== '/' &&
        path.startsWith(`${item.path}/`) &&
        item.path.length > (best?.length ?? 0)
      ) {
        best = { group, length: item.path.length };
      }
    }
  }
  return best?.group;
}
